import { useState, useEffect, useMemo, useRef, memo } from 'react'
import { createPortal } from 'react-dom'
import { getSavedListsCache } from './savedListsCache'
import { authenticatedFetch, getAuthHeaders } from './utils/auth.js'
import {
  invalidateSavedListsCache,
  notifySavedListsInvalidated,
  SAVED_LISTS_INVALIDATE_EVENT,
} from './savedListsCache.js'
import { useAuth } from './contexts/AuthContext.jsx'
import { useToast } from './hooks/useToast.js'
import { importRecipeFromUrl, buildIntentFromUrl, isRecipeSiteAuthErrorMsg, parseRecipeSiteAuthDomain, importRecipeFromText, formatRecipeReimportMessage } from './utils/recipeImport.js'
import {
  trackRecipeImportEvent,
  RECIPE_IMPORT_SURFACES,
  missingFieldsAsProps,
} from './utils/recipeImportAnalytics.js'
import RecipeAuthModal from './components/RecipeAuthModal.jsx'
import { formatTotalTimeLabel, getSavedListTotalMinutes } from './utils/recipeMetadataTime.js'
import { isSavedListRecipe } from './utils/savedListRecipe.js'
import { trackUiEvent } from './utils/uiAnalytics.js'
import {
  trackRecipePackEvent,
  trackRecipePackShelfViewedOnce,
  packTelemetryIds,
  RECIPE_PACK_SURFACES,
} from './utils/recipePackAnalytics.js'
import { buildRecipeDetailUrl, navigateToPlan } from './utils/appNavigation.js'
import {
  consumeCookBrowseRestore,
  normalizeCookBrowseFilters,
  saveCookBrowseRestore,
} from './utils/cookBrowseRestore.js'
import {
  setCookPreferencesEnabled,
} from './utils/cookPreferencesSetting.js'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from './design-system/primitives/index.js'
import { FilterChipToggle } from './design-system/patterns/FilterChip.jsx'
import CookFilterModal from './components/cook/CookFilterModal.jsx'
import CookBrowseBackToTop from './components/cook/CookBrowseBackToTop.jsx'
import CookPageSkeleton from './components/cook/CookPageSkeleton.jsx'
import {
  CUISINE_OPTIONS,
  HEALTHINESS_OPTIONS,
  TIME_OPTIONS,
} from './components/cook/cookFilterConstants.js'
import StateBanner from './components/StateBanner/StateBanner.jsx'
import StateBannerStack from './components/StateBanner/StateBannerStack.jsx'
import {
  AddViewMature,
  MobileAddMature,
  PackDetailModal, PackDetailSheet,
  PackFilterChips, BrowseThinPrompt,
  PackToast, PackBadge,
  PACKS_FULL,
  THIN_LIBRARY_THRESHOLD,
} from './components/RecipePacks/index.js'
import { resolveCuratedPacks, getSavedListPackId, orderPacksForAddDisplay } from './components/RecipePacks/resolveCuratedPacks.js'
import {
  addWeeksToMonday,
  currentWeekStartIso,
  planAssignmentChipLabel,
  planWeekKindForRecipe,
  recipeIdsFromPlanItems,
} from './utils/mealPlanHelpers.js'
import './components/planner/planner.css'

const savedListsCache = getSavedListsCache()
const HIDE_RECIPE_PACK_FILTER_BAR = ['1', 'true', 'yes', 'on'].includes(
  String(import.meta.env.VITE_HIDE_RECIPE_PACK_FILTER_BAR ?? 'true').toLowerCase()
)

// Breakpoint (px) below which the consolidated filter modal is shown instead of inline filters
const MOBILE_FILTER_BREAKPOINT = 768

// Gradient palette for recipe card bands — no recipe photography yet, so each card gets a
// deterministic color assigned from its id/name (stable across renders, no two recipes flicker
// between colors on refetch).
// Canonical Browse filter row tabs (single-select) — recipes-pack-desktop.jsx BrowseFilterRow.
const BROWSE_TABS = [
  { id: 'cookable', label: 'Cookable now' },
]

const MOBILE_BROWSE_TABS = [
  { id: 'cookable', label: 'Cookable now' },
]

/** Canonical palette — BROWSE_CARD_SPEC / recipes-cook-redesign RCD_GRADIENTS */
const RECIPE_CARD_GRADIENT_KEYS = [
  'r-citrus', 'r-fish', 'r-tomato', 'r-greens', 'r-mush', 'r-curry',
  'r-spice', 'r-cream', 'r-grain', 'r-herb', 'r-bean', 'r-leaf',
]

function getRecipeCardGradientKey(list) {
  const meta = list?.list_metadata && typeof list.list_metadata === 'object'
    ? list.list_metadata
    : {}
  const stored = String(meta.gradient || meta.recipe_gradient || '').trim()
  if (stored && RECIPE_CARD_GRADIENT_KEYS.includes(stored)) return stored

  const seed = String(list?.id ?? list?.name ?? '')
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0
  }
  const index = Math.abs(hash) % RECIPE_CARD_GRADIENT_KEYS.length
  return RECIPE_CARD_GRADIENT_KEYS[index] || 'r-citrus'
}

function getRecipeCardServings(meta = {}) {
  const raw = meta.servings ?? meta.serves
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : null
}

function isImageImportedRecipeMeta(meta = {}) {
  const importMethod = String(meta.import_method || '').trim().toLowerCase()
  if (['image', 'photo', 'upload_image', 'ocr'].includes(importMethod)) return true
  const source = String(meta.source || '').trim().toLowerCase()
  if (['recipe_image_upload', 'image', 'photo'].includes(source)) return true
  if (meta.extraction_method && !meta.source_url) return true
  return false
}

function isPasteImportedRecipeMeta(meta = {}) {
  const importMethod = String(meta.import_method || '').trim().toLowerCase()
  if (['paste', 'text', 'paste_text'].includes(importMethod)) return true
  const source = String(meta.source || '').trim().toLowerCase()
  return source === 'paste_text' || source === 'paste'
}

function isAiGeneratedRecipeMeta(meta = {}) {
  if (isImageImportedRecipeMeta(meta) || isPasteImportedRecipeMeta(meta)) return false
  if (meta.generated_by_ai === true) return true
  if (['ai', 'agent', 'curator'].includes(meta.created_by)) return true
  const source = String(meta.source || '').trim().toLowerCase()
  if (['ai', 'agent', 'captain_cook', 'recipe_agent'].includes(source)) return true
  const importMethod = String(meta.import_method || '').trim().toLowerCase()
  if (['ai', 'agent', 'generated'].includes(importMethod)) return true
  if (meta.source_url) return false
  if (meta.is_recipe === true || String(meta.is_recipe).toLowerCase() === 'true') return true
  if (meta.recipe_name && !meta.source_domain) return true
  return false
}

function getRecipeAuthorName(meta = {}) {
  const direct = typeof meta.author === 'string' ? meta.author.trim() : ''
  if (direct) return direct

  const raw = meta.raw_metadata && typeof meta.raw_metadata === 'object'
    ? meta.raw_metadata
    : {}

  const rawAuthor = raw.author
  if (typeof rawAuthor === 'string' && rawAuthor.trim()) return rawAuthor.trim()
  if (rawAuthor && typeof rawAuthor === 'object' && typeof rawAuthor.name === 'string' && rawAuthor.name.trim()) {
    return rawAuthor.name.trim()
  }
  if (Array.isArray(rawAuthor) && rawAuthor.length > 0) {
    const first = rawAuthor[0]
    if (typeof first === 'string' && first.trim()) return first.trim()
    if (first && typeof first === 'object' && typeof first.name === 'string' && first.name.trim()) {
      return first.name.trim()
    }
  }

  const publisherName = raw.publisher && typeof raw.publisher === 'object' && typeof raw.publisher.name === 'string'
    ? raw.publisher.name.trim()
    : ''
  if (publisherName) return publisherName

  return ''
}

function getRecipeCardSourceType(list, pack) {
  if (pack?.name) return 'pack'
  const meta = list?.list_metadata && typeof list.list_metadata === 'object'
    ? list.list_metadata
    : {}
  if (isImageImportedRecipeMeta(meta)) return 'imported-photo'
  if (isPasteImportedRecipeMeta(meta)) return 'imported-text'
  if (isAiGeneratedRecipeMeta(meta)) return 'ai'
  const source = String(meta.source || '').trim().toLowerCase()
  if (source === 'family' || source === 'family_recipe') return 'family'
  const tags = Array.isArray(list?.tags) ? list.tags : []
  if (tags.some((t) => String(t).trim().toLowerCase() === 'family recipe')) return 'family'
  const importMethod = String(meta.import_method || '').trim().toLowerCase()
  const authorName = getRecipeAuthorName(meta)
  if (meta.source_url || importMethod === 'url') return authorName ? 'website' : 'imported-link'
  if (authorName) return 'website'
  return null
}

function getRecipeCardStatusChip(pantryMatch, pantryMatchMode, {
  isInPlan = false,
  isInPicks = false,
  planWeekKind = null,
} = {}) {
  const planLabel = planAssignmentChipLabel(
    planWeekKind || (isInPlan ? 'current' : null),
    { inPicks: isInPicks },
  )
  if (planLabel) {
    return { label: planLabel, variant: 'plan' }
  }
  if (!pantryMatchMode || !pantryMatch) return null
  if (pantryMatch.bucket === 'Cook Now') {
    return { label: 'Cookable now', variant: 'green' }
  }
  if (pantryMatch.bucket === 'Almost There') {
    const missing = Math.max(0, (pantryMatch.total_count || 0) - (pantryMatch.matched_count || 0))
    if (missing <= 0) return null
    return { label: missing === 1 ? '1 to shop' : `${missing} to shop`, variant: 'orange' }
  }
  return null
}

function renderRecipePantryMissingLine(pantryMatch) {
  const coreMiss = Array.isArray(pantryMatch.missing_high_conf_core)
    ? pantryMatch.missing_high_conf_core.filter(Boolean)
    : []
  const otherMiss = Array.isArray(pantryMatch.missing_other)
    ? pantryMatch.missing_other.filter(Boolean)
    : []
  const allMissing = [...coreMiss, ...otherMiss]
  if (allMissing.length === 0) return null
  const preview = allMissing.slice(0, 2)
  const extra = allMissing.length - preview.length
  return (
    <span>
      Missing: {preview.join(', ')}
      {extra > 0 ? ` + ${extra} more` : ''}
    </span>
  )
}

function renderRecipeSourceLabel(list, pack) {
  const meta = list.list_metadata || {}
  const authorName = getRecipeAuthorName(meta)
  const sourceType = getRecipeCardSourceType(list, pack)

  if (sourceType === 'pack' && pack?.name) {
    return (
      <div className="recipe-card-source">
        <PackBadge pack={pack} size={10} />
      </div>
    )
  }
  if (sourceType === 'ai') {
    return <span className="recipe-card-source recipe-card-source--ai">✦ AI generated</span>
  }
  if (sourceType === 'family') {
    return <span className="recipe-card-source recipe-card-source--plain">Family recipe</span>
  }
  if (sourceType === 'imported-photo') {
    return (
      <div className="recipe-card-source">
        <i className="fas fa-camera icon-sm icon-muted" aria-hidden="true" />
        <span className="recipe-card-source-muted">Imported via image</span>
      </div>
    )
  }
  if (sourceType === 'imported-text') {
    return (
      <div className="recipe-card-source">
        <i className="fas fa-align-left icon-sm icon-muted" aria-hidden="true" />
        <span className="recipe-card-source-muted">Imported from text</span>
      </div>
    )
  }
  if (sourceType === 'imported-link') {
    return (
      <div className="recipe-card-source">
        <i className="fas fa-globe icon-sm icon-muted" aria-hidden="true" />
        <span className="recipe-card-source-muted">Saved link</span>
      </div>
    )
  }
  if (authorName) {
    return (
      <div className="recipe-card-source">
        <i className="fas fa-user icon-sm icon-muted" aria-hidden="true" />
        <span className="recipe-card-source-name">{authorName}</span>
      </div>
    )
  }
  if (meta.source_url) {
    return <span className="recipe-card-source recipe-card-source--plain">Imported</span>
  }
  return null
}

// Matches backend/services/recipe_import_job.py VALID_COOKING_TECHNIQUES — hidden on browse cards
const COOKING_TECHNIQUE_TAGS = new Set([
  'grilling',
  'baking',
  'air frying',
  'frying',
  'roasting',
  'broiling',
  'sautéing',
  'sauteing',
  'steaming',
  'boiling',
  'slow cooking',
  'pressure cooking',
  'stir-frying',
  'stir frying',
  'braising',
  'poaching',
  'smoking',
])

function isCookingTechniqueTag(tag) {
  if (tag == null) return false
  const s = String(tag).trim().toLowerCase()
  return COOKING_TECHNIQUE_TAGS.has(s)
}

function normalizePreferenceTerm(value) {
  return String(value || '').trim().toLowerCase()
}

function normalizeStringList(value) {
  if (!Array.isArray(value)) return []
  return value
    .filter((x) => typeof x === 'string')
    .map((x) => x.trim())
    .filter(Boolean)
}

function listMatchesPreferenceExclusion(list, rawTerms) {
  const terms = Array.isArray(rawTerms)
    ? rawTerms.map(normalizePreferenceTerm).filter(Boolean)
    : []
  if (terms.length === 0) return false

  const metadata = list?.list_metadata && typeof list.list_metadata === 'object'
    ? list.list_metadata
    : {}
  const itemNames = Array.isArray(list?.items)
    ? list.items
        .map((item) => {
          if (!item) return ''
          if (typeof item === 'string') return item
          if (typeof item === 'object') {
            return item.name || item.item_name || item.ingredient || item.ingredient_name || ''
          }
          return ''
        })
        .filter(Boolean)
    : []

  const searchParts = [
    list?.name,
    list?.description,
    metadata.cuisine,
    metadata.author,
    metadata.recipe_name,
    ...(Array.isArray(list?.tags) ? list.tags : []),
    ...itemNames,
  ]
  const searchableText = searchParts
    .map((part) => String(part || '').toLowerCase().trim())
    .filter(Boolean)
    .join(' ')

  return terms.some((term) => {
    const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const normalizedPhrase = escaped.replace(/\s+/g, '\\s+')
    const pattern = new RegExp(`(^|\\W)${normalizedPhrase}(\\W|$)`, 'i')
    return pattern.test(searchableText)
  })
}

/** Single cuisine string for recipe meta row: metadata wins, else first known cuisine tag */
function getRecipeCardDisplayCuisine(list, cuisineNamesForTags) {
  const fromMeta = String(list.list_metadata?.cuisine || '').trim()
  if (fromMeta) return fromMeta
  const tags = list.tags || []
  for (const tag of tags) {
    if (cuisineNamesForTags.some((c) => c.toLowerCase() === String(tag).toLowerCase())) {
      return String(tag).trim()
    }
  }
  return ''
}

/** Health / cuisine / prep tags — recipe cards use prototype semantic classes */
function renderSavedListTagSection(list, isRecipeCard, cuisineNamesForTags, pack = null) {
  const healthTierLabels = {
    junk_food_delicious: 'Junk food',
    wont_clog_arteries: "Won't clog arteries",
    straight_up_healthy: 'Healthy',
  }
  const healthTier = list.list_metadata?.health_tier
  const healthLabel = healthTier ? healthTierLabels[healthTier] || healthTier : null
  const hasTags = list.tags && list.tags.length > 0
  if (!healthLabel && !hasTags) return null

  let cuisineTagsInList = hasTags
    ? list.tags.filter((tag) => cuisineNamesForTags.some((c) => c.toLowerCase() === String(tag).toLowerCase()))
    : []
  if (isRecipeCard && cuisineTagsInList.length > 0) {
    const display = getRecipeCardDisplayCuisine(list, cuisineNamesForTags)
    const d = display.trim().toLowerCase()
    if (d) {
      cuisineTagsInList = cuisineTagsInList.filter(
        (tag) => String(tag).trim().toLowerCase() !== d
      )
    }
  }
  let otherTags = hasTags
    ? list.tags.filter((tag) => !cuisineNamesForTags.some((c) => c.toLowerCase() === String(tag).toLowerCase()))
    : []
  if (list.list_metadata?.source_url) {
    otherTags = otherTags.filter((tag) => typeof tag === 'string' && !tag.toLowerCase().startsWith('source:'))
  }
  otherTags = otherTags.filter((tag) => typeof tag !== 'string' || tag.toLowerCase() !== 'recipe')
  if (isRecipeCard && pack?.name) {
    const packNameLower = String(pack.name).trim().toLowerCase()
    otherTags = otherTags.filter(
      (tag) => String(tag).trim().toLowerCase() !== packNameLower
    )
  }
  otherTags = otherTags.filter((tag) => !isCookingTechniqueTag(tag))

  const MAX_OTHER_TAGS = 3
  const visibleOtherTags = otherTags.slice(0, MAX_OTHER_TAGS)

  const wrapClass = isRecipeCard ? 'recipe-card-tags' : 'saved-list-legacy-tags-wrap'

  const healthTagClass = isRecipeCard
    ? `recipe-card-tag ${healthTier === 'junk_food_delicious' ? 'recipe-card-tag--personality' : 'recipe-card-tag--health'}`
    : 'badge badge-health'

  return (
    <div className={wrapClass}>
      {healthLabel && (
        <span className={healthTagClass} title={list.list_metadata?.health_tier_reason || undefined}>
          {healthLabel}
        </span>
      )}
      {cuisineTagsInList.map((tag, idx) => (
        <span
          key={`cuisine-${idx}`}
          className={isRecipeCard ? 'recipe-card-tag recipe-card-tag--cuisine' : 'badge badge-cuisine'}
        >
          {tag}
        </span>
      ))}
      {visibleOtherTags.map((tag, idx) => (
        <span
          key={`tag-${idx}`}
          className={
            isRecipeCard
              ? 'recipe-card-tag recipe-card-tag--misc'
              : 'badge badge-tag'
          }
        >
          {tag}
        </span>
      ))}
    </div>
  )
}

/**
 * Permanent browse result count under the filter row, with an optional Preferences
 * setting (not a filter chip) that expands to show saved preference tokens.
 */
function BrowseResultCountBar({
  shown,
  total,
  preferencesAvailable = false,
  preferencesEnabled = false,
  preferencesExpanded = false,
  onTogglePreferencesExpanded,
  onTogglePreferencesEnabled,
  preferenceTokens = [],
  onManagePreferences,
}) {
  return (
    <div className="cook-browse-result-count">
      <div className="cook-browse-result-count-row">
        <span className="cook-browse-result-count-label" aria-live="polite">
          Showing <strong>{shown}</strong> of {total}
        </span>
        {preferencesAvailable && (
          <button
            type="button"
            className={`cook-browse-pref-setting${preferencesEnabled ? ' is-enabled' : ''}${preferencesExpanded ? ' is-expanded' : ''}`}
            onClick={onTogglePreferencesExpanded}
            aria-expanded={preferencesExpanded}
            aria-controls="cook-browse-pref-panel"
          >
            <span>
              Preferences:{' '}
              <strong>{preferencesEnabled ? 'Enabled' : 'Off'}</strong>
            </span>
            <i
              className={`fas fa-chevron-${preferencesExpanded ? 'up' : 'down'} cook-browse-pref-setting-caret`}
              aria-hidden="true"
            />
          </button>
        )}
      </div>
      {preferencesAvailable && preferencesExpanded && (
        <div id="cook-browse-pref-panel" className="pref-summary-bar">
          <div className="pref-summary-tokens">
            {preferenceTokens.map((token, i) => (
              <span
                key={`${token.kind}-${token.label}-${i}`}
                className={`pref-token${token.kind === 'allergy' ? ' pref-token--allergy' : ''}`}
              >
                <span className="pref-token-kind">{token.kind === 'allergy' ? '⊘ Allergy' : 'Dislike'}</span>
                {token.label}
                {token.note ? <span className="pref-token-note"> · {token.note}</span> : null}
              </span>
            ))}
          </div>
          <div className="pref-summary-actions">
            <button
              type="button"
              className="pref-summary-toggle"
              onClick={onTogglePreferencesEnabled}
              aria-pressed={preferencesEnabled}
            >
              {preferencesEnabled ? 'Turn off' : 'Turn on'}
            </button>
            {typeof onManagePreferences === 'function' && (
              <button type="button" className="pref-summary-manage" onClick={onManagePreferences}>
                Manage in Account →
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function queueScrollCookPageToTop() {
  const scrollToTop = () => {
    const scrollRoot = document.querySelector('.main-content')
    if (!scrollRoot) return
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (typeof scrollRoot.scrollTo === 'function') {
      scrollRoot.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' })
    } else {
      scrollRoot.scrollTop = 0
    }
  }
  requestAnimationFrame(() => requestAnimationFrame(scrollToTop))
}

/** Icon-only + at rest; expands to "Add to plan" on hover/focus (desktop). Mobile always shows "+ Plan". */
function RecipeCardAddToPlanButton({
  onClick,
  disabled = false,
  isMobile = false,
  recipeName = 'recipe',
}) {
  if (isMobile) {
    return (
      <button
        type="button"
        className="recipe-card-btn-plan recipe-card-btn-plan--mobile"
        title="Add to this week's plan"
        aria-label={`Add ${recipeName} to this week's meal plan`}
        onClick={onClick}
        disabled={disabled}
      >
        <span className="recipe-card-btn-plan-icon" aria-hidden="true">+</span>
        <span className="recipe-card-btn-plan-label">Plan</span>
      </button>
    )
  }

  return (
    <button
      type="button"
      className="recipe-card-btn-plan"
      title="Add to this week's plan"
      aria-label={`Add ${recipeName} to this week's meal plan`}
      onClick={onClick}
      disabled={disabled}
    >
      <span className="recipe-card-btn-plan-icon" aria-hidden="true">+</span>
      <span className="recipe-card-btn-plan-label">Add to plan</span>
    </button>
  )
}

/** Sort lists: favorites first, then by most recently added (created_at, else updated_at) descending */
function sortListsByFavoriteAndRecent(lists) {
  return [...lists].sort((a, b) => {
    if (a.is_favorite && !b.is_favorite) return -1
    if (!a.is_favorite && b.is_favorite) return 1
    const tA = new Date(a.created_at || a.updated_at || 0).getTime()
    const tB = new Date(b.created_at || b.updated_at || 0).getTime()
    return tB - tA
  })
}

function SavedListsManager({
  onLoadList: _onLoadList,
  selectedStore: _selectedStore,
  setShowLoginModal,
  onRecipeImageSelected,
  onFeaturedPromptSelect: _onFeaturedPromptSelect,
  onPantryShoppingBannerFromMatch,
  onViewRecipe,
  onManagePreferences,
  pageNotices = null,
  pageNoticesActive = false,
  guestMode = false,
  hidePageHeader = false,
  hideGetRecipesNav = false,
  defaultGetRecipesMethod,
}) {
  void _onFeaturedPromptSelect
  void _onLoadList
  void _selectedStore
  const { isAuthenticated: accountAuthenticated, isLoading: authLoading, user } = useAuth()
  const isAuthenticated = accountAuthenticated || guestMode
  const { showToast } = useToast()
  const browseRestoreRef = useRef(consumeCookBrowseRestore())
  const initialBrowseRestore = browseRestoreRef.current
  const initialBrowseFilters = useMemo(
    () => normalizeCookBrowseFilters(initialBrowseRestore?.filters),
    [initialBrowseRestore],
  )
  const pendingBrowseRestoreRef = useRef(initialBrowseRestore)
  const [savedLists, setSavedLists] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [preferences, setPreferences] = useState(null)
  const [preferencesLoaded, setPreferencesLoaded] = useState(false)
  const [searchTerm, setSearchTerm] = useState(() => initialBrowseFilters.searchTerm)
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(() => initialBrowseFilters.showFavoritesOnly)
  const [selectedCuisines, setSelectedCuisines] = useState(() => initialBrowseFilters.selectedCuisines)
  const [showCuisineDropdown, setShowCuisineDropdown] = useState(false)
  const [selectedHealthTiers, setSelectedHealthTiers] = useState(() => initialBrowseFilters.selectedHealthTiers) // slugs: junk_food_delicious, wont_clog_arteries, straight_up_healthy
  const [showDietDropdown, setShowDietDropdown] = useState(false)
  const [selectedMaxTime, setSelectedMaxTime] = useState(() => initialBrowseFilters.selectedMaxTime) // null = any, number = max minutes
  const [showTimeDropdown, setShowTimeDropdown] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const [renamingId, setRenamingId] = useState(null)
  const [editingList, setEditingList] = useState(null) // { id, name, description, tags }
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editCuisine, setEditCuisine] = useState('')
  const [editLastCookedDate, setEditLastCookedDate] = useState('')
  const [editAuthor, setEditAuthor] = useState('')
  const [refreshAfterImport, setRefreshAfterImport] = useState(false)
  const [scrollToImportedListId, setScrollToImportedListId] = useState(null)
  const [highlightImportedListId, setHighlightImportedListId] = useState(null)
  const cuisineDropdownRef = useRef(null)
  const dietDropdownRef = useRef(null)
  const timeDropdownRef = useRef(null)

  // Mobile: consolidated filter modal + always-visible library search
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= MOBILE_FILTER_BREAKPOINT)
  const mobileSearchInputRef = useRef(null)
  const [showFilterModal, setShowFilterModal] = useState(false)
  const [filterCount, setFilterCount] = useState(null)
  const [filterCountLoading, setFilterCountLoading] = useState(false)
  const [filterOptions, setFilterOptions] = useState(null) // { cuisines, chefs, health_tiers, time_options } per-option counts
  const [_filterOptionsLoading, setFilterOptionsLoading] = useState(false)
  const filterOptionsDebounceRef = useRef(null)

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_FILTER_BREAKPOINT}px)`)
    const handler = () => setIsMobile(mql.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  // Load preferences (used for dynamic "kid preference" prompt cards in Recipes).
  useEffect(() => {
    let cancelled = false

    async function loadPreferences() {
      if (!isAuthenticated) {
        setPreferences(null)
        setPreferencesLoaded(false)
        return
      }
      try {
        const res = await authenticatedFetch('/preferences/me', { headers: getAuthHeaders() })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`)
        if (!cancelled) {
          setPreferences(data.preferences || {})
          setPreferencesLoaded(true)
        }
      } catch (e) {
        // Preferences are optional; if they fail, we simply omit preference-based cards.
        console.warn('[SavedListsManager] Failed to load preferences for featured prompts:', e)
        if (!cancelled) {
          setPreferences(null)
          setPreferencesLoaded(true)
        }
      }
    }

    if (!authLoading && isAuthenticated) {
      loadPreferences()
    }

    return () => { cancelled = true }
  }, [authLoading, isAuthenticated])

  // Saved preferences for the "✦ My preferences" chip — the account owner's own allergies/dislikes
  // plus household members' (folded in per design: no separate kid-pref chip row, just attributed
  // tokens in the summary bar, e.g. "Shellfish (allergy)" vs "Spicy foods (dislike · Lila)").
  const selfPrefTokens = useMemo(() => {
    if (!preferencesLoaded) return []
    const dietary = preferences?.dietary && typeof preferences.dietary === 'object' ? preferences.dietary : {}
    const allergies = normalizeStringList(dietary.allergies)
    const dislikes = normalizeStringList(dietary.dislikes)
    const tokens = [
      ...allergies.map((label) => ({ label, kind: 'allergy' })),
      ...dislikes.map((label) => ({ label, kind: 'dislike' })),
    ]
    const familyMembers = preferences?.family_members && typeof preferences.family_members === 'object'
      ? preferences.family_members
      : {}
    Object.entries(familyMembers).forEach(([name, member]) => {
      const memberDietary = member?.dietary && typeof member.dietary === 'object' ? member.dietary : {}
      normalizeStringList(memberDietary.allergies).forEach((label) => {
        tokens.push({ label, kind: 'allergy', note: name })
      })
      normalizeStringList(memberDietary.dislikes).forEach((label) => {
        tokens.push({ label, kind: 'dislike', note: name })
      })
    })
    return tokens
  }, [preferences, preferencesLoaded])

  // State for recipe page UI controls (must be declared before useMemos that reference them)
  const [openOverflowId, setOpenOverflowId] = useState(null) // recipe card overflow menu
  const [mealPlanRecipeIds, setMealPlanRecipeIds] = useState(() => new Set())
  const [mealPlanNextWeekRecipeIds, setMealPlanNextWeekRecipeIds] = useState(() => new Set())
  const [mealPlanCandidateIds, setMealPlanCandidateIds] = useState(() => new Set())

  useEffect(() => {
    const raw = preferences?.meal_plan?.candidate_recipe_ids
    if (!Array.isArray(raw)) return
    setMealPlanCandidateIds(new Set(raw.map((id) => String(id)).filter(Boolean)))
  }, [preferences])

  useEffect(() => {
    if (!isAuthenticated) {
      setMealPlanRecipeIds(new Set())
      setMealPlanNextWeekRecipeIds(new Set())
      setMealPlanCandidateIds(new Set())
      return undefined
    }

    let cancelled = false

    async function loadPlanMembership() {
      try {
        const currentStart = currentWeekStartIso()
        const nextStart = addWeeksToMonday(currentStart, 1)
        const [curRes, nextRes] = await Promise.all([
          authenticatedFetch('/meal-plans/current'),
          authenticatedFetch(`/meal-plans/current?week_start=${encodeURIComponent(nextStart)}`),
        ])
        if (cancelled) return
        if (curRes.ok) {
          const plan = await curRes.json()
          setMealPlanRecipeIds(recipeIdsFromPlanItems(plan?.items))
        }
        if (nextRes.ok) {
          const plan = await nextRes.json()
          setMealPlanNextWeekRecipeIds(recipeIdsFromPlanItems(plan?.items))
        }
      } catch {
        /* meal plan optional for browse cards */
      }
    }

    void loadPlanMembership()

    const onPlanUpdated = (event) => {
      const plan = event?.detail?.plan
      if (plan?.items && plan?.week_start_date) {
        const currentStart = currentWeekStartIso()
        const nextStart = addWeeksToMonday(currentStart, 1)
        const ids = recipeIdsFromPlanItems(plan.items)
        if (plan.week_start_date === currentStart) setMealPlanRecipeIds(ids)
        else if (plan.week_start_date === nextStart) setMealPlanNextWeekRecipeIds(ids)
        else void loadPlanMembership()
      } else {
        void loadPlanMembership()
      }
    }
    window.addEventListener('mealPlanCurrentUpdated', onPlanUpdated)

    return () => {
      cancelled = true
      window.removeEventListener('mealPlanCurrentUpdated', onPlanUpdated)
    }
  }, [isAuthenticated])

  const recipeLists = useMemo(
    () => savedLists.filter((list) => isSavedListRecipe(list)),
    [savedLists]
  )

  const hasServerSideListFilters = Boolean(
    searchTerm ||
    showFavoritesOnly ||
    selectedCuisines.length > 0 ||
    selectedHealthTiers.length > 0 ||
    selectedMaxTime != null
  )

  /** Full-library recipe lists — not reduced by server-side browse filters (uses cache when filtered). */
  const fullLibraryRecipeLists = useMemo(() => {
    const currentUserId = user?.id ?? null
    const cacheLists =
      savedListsCache.lists?.length > 0 && savedListsCache.userId === currentUserId
        ? savedListsCache.lists
        : null
    const source = hasServerSideListFilters && cacheLists ? cacheLists : savedLists
    return sortListsByFavoriteAndRecent(
      source.filter((list) => isSavedListRecipe(list))
    )
  }, [savedLists, hasServerSideListFilters, user?.id])

  /** Full-library recipe count — not reduced by server-side browse filters (search, cuisine, etc.). */
  const fullLibraryRecipeCount = useMemo(
    () => fullLibraryRecipeLists.length,
    [fullLibraryRecipeLists]
  )

  // Browse filter row (canonical: recipes-pack-desktop.jsx BrowseFilterRow) — single-select tabs.
  // Tabs reuse existing filter state (selectedMaxTime, pantryMatchMode) so a tab selection
  // sets one filter and clears the others.
  const sortedRecipeLists = useMemo(
    () => sortListsByFavoriteAndRecent(recipeLists),
    [recipeLists]
  )
  // Preferences apply as a browse setting (near result count), not a filter chip.
  // Persisted in localStorage so Enabled/Off survives sessions.
  const [prefFilterOn, setPrefFilterOnState] = useState(() => initialBrowseFilters.prefFilterOn)
  const setPrefFilterOn = (nextOrUpdater) => {
    setPrefFilterOnState((prev) => {
      const next = typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater
      setCookPreferencesEnabled(Boolean(next))
      return Boolean(next)
    })
  }
  const [prefPanelExpanded, setPrefPanelExpanded] = useState(false)
  const [pantryMatchMode, setPantryMatchMode] = useState(() => initialBrowseFilters.pantryMatchMode)
  const [pantryMatchLoading, setPantryMatchLoading] = useState(false)
  const [pantryMatchError, setPantryMatchError] = useState('')
  const [pantryMatchByListId, setPantryMatchByListId] = useState({})
  const [pantryMatchOrderedIds, setPantryMatchOrderedIds] = useState([])

  // Cook IA: two methods — "Browse saved" (search/filters + grid) or "Add recipe" (import workflows)
  const [getRecipesMethod, setGetRecipesMethod] = useState(() => (
    defaultGetRecipesMethod === 'add' || defaultGetRecipesMethod === 'browse'
      ? defaultGetRecipesMethod
      : initialBrowseFilters.getRecipesMethod
  )) // 'browse' | 'add'
  const visibleGetRecipesMethod = hideGetRecipesNav ? 'add' : getRecipesMethod
  const onboardingAdd = hidePageHeader && hideGetRecipesNav

  useEffect(() => {
    if (hideGetRecipesNav && getRecipesMethod !== 'add') setGetRecipesMethod('add')
  }, [hideGetRecipesNav, getRecipesMethod])

  // Recipe Packs state
  const [addedPackIds, setAddedPackIds] = useState(new Set())     // optimistic pack ids while saved lists refresh
  const [openPackId, setOpenPackId] = useState(null)              // pack detail modal/sheet open
  const [packToast, setPackToast] = useState(null)                // { packId, addedCount, createdIds, refreshedIds, undoBatchId } | null
  const [undoingPackId, setUndoingPackId] = useState(null)
  const [activePackFilter, setActivePackFilter] = useState(() => initialBrowseFilters.activePackFilter)  // pack id to filter Browse by
  const [livePacks, setLivePacks] = useState(null)                // fetched from GET /curated-packs
  const packEventSurfaceRef = useRef(RECIPE_PACK_SURFACES.COOK_ADD)
  const packShelfViewedRef = useRef(new Set())

  const resolvedPacks = useMemo(() => resolveCuratedPacks(livePacks), [livePacks])

  /** Stable random order for Add Recipes pack grid (avoids bottom-of-list visibility bias). */
  const addPackOrderIdsRef = useRef(null)
  const shuffledAddPacks = useMemo(
    () => orderPacksForAddDisplay(resolvedPacks, addPackOrderIdsRef),
    [resolvedPacks],
  )

  const resolvedPackById = useMemo(
    () => Object.fromEntries(
      resolvedPacks.flatMap((p) => [
        [p.id, p],
        [p.slug, p],
      ].filter(([key]) => key))
    ),
    [resolvedPacks]
  )

  const savedListAddedPackIds = useMemo(() => {
    const ids = new Set()
    recipeLists.forEach((list) => {
      const rawPackId = String(getSavedListPackId(list) || '').trim()
      if (!rawPackId) return
      const pack = resolvedPackById[rawPackId]
      ids.add(rawPackId)
      if (pack?.id) ids.add(pack.id)
      if (pack?.slug) ids.add(pack.slug)
    })
    return ids
  }, [recipeLists, resolvedPackById])

  const visibleAddedPackIds = useMemo(() => {
    const ids = new Set(savedListAddedPackIds)
    addedPackIds.forEach((id) => ids.add(id))
    return ids
  }, [savedListAddedPackIds, addedPackIds])

  const browsePackFilter = HIDE_RECIPE_PACK_FILTER_BAR ? null : activePackFilter

  const packFilteredRecipeLists = useMemo(() => {
    if (!browsePackFilter) return sortedRecipeLists
    const activePack = resolvedPackById[browsePackFilter]
    const packIds = new Set(
      [browsePackFilter, activePack?.id, activePack?.slug]
        .map((id) => String(id || '').trim())
        .filter(Boolean)
    )
    return sortedRecipeLists.filter((list) => packIds.has(String(getSavedListPackId(list) || '').trim()))
  }, [sortedRecipeLists, browsePackFilter, resolvedPackById])

  const packFilteredFullLibraryLists = useMemo(() => {
    if (!browsePackFilter) return fullLibraryRecipeLists
    const activePack = resolvedPackById[browsePackFilter]
    const packIds = new Set(
      [browsePackFilter, activePack?.id, activePack?.slug]
        .map((id) => String(id || '').trim())
        .filter(Boolean)
    )
    return fullLibraryRecipeLists.filter((list) => packIds.has(String(getSavedListPackId(list) || '').trim()))
  }, [fullLibraryRecipeLists, browsePackFilter, resolvedPackById])

  // Preferences setting: when Enabled, exclude recipes matching saved allergies/dislikes.
  // Applies to All Recipes and Cookable now (pantry match) the same way — Off means no pref filtering.
  const prefFilteredRecipeLists = useMemo(() => {
    if (!prefFilterOn || selfPrefTokens.length === 0) return packFilteredRecipeLists
    const terms = selfPrefTokens.map((t) => t.label)
    return packFilteredRecipeLists.filter((list) => !listMatchesPreferenceExclusion(list, terms))
  }, [packFilteredRecipeLists, prefFilterOn, selfPrefTokens])

  const prefFilteredFullLibraryLists = useMemo(() => {
    if (!prefFilterOn || selfPrefTokens.length === 0) return packFilteredFullLibraryLists
    const terms = selfPrefTokens.map((t) => t.label)
    return packFilteredFullLibraryLists.filter((list) => !listMatchesPreferenceExclusion(list, terms))
  }, [packFilteredFullLibraryLists, prefFilterOn, selfPrefTokens])

  const visibleRecipeLists = useMemo(() => {
    if (!pantryMatchMode) return prefFilteredRecipeLists
    const byId = new Map(
      prefFilteredFullLibraryLists.map((list) => [String(list.id), list])
    )
    return pantryMatchOrderedIds
      .map((id) => {
        const row = pantryMatchByListId[String(id)]
        if (!row || row.bucket === 'Hidden') return null
        return byId.get(String(id)) ?? null
      })
      .filter(Boolean)
  }, [
    prefFilteredRecipeLists,
    prefFilteredFullLibraryLists,
    pantryMatchMode,
    pantryMatchByListId,
    pantryMatchOrderedIds,
  ])
  const pantryMatchRows = useMemo(
    () => Object.values(pantryMatchByListId || {}).filter(Boolean),
    [pantryMatchByListId]
  )
  // Cookable-now counts follow the Preferences setting (same set as visible cards).
  const preferenceAwarePantryMatchRows = useMemo(() => {
    if (!prefFilterOn || selfPrefTokens.length === 0) return pantryMatchRows
    const allowedIds = new Set(prefFilteredFullLibraryLists.map((list) => String(list.id)))
    return pantryMatchRows.filter((row) => {
      const id = row?.saved_list_id != null ? String(row.saved_list_id) : ''
      return id && allowedIds.has(id)
    })
  }, [pantryMatchRows, prefFilterOn, selfPrefTokens, prefFilteredFullLibraryLists])
  const cookableNowCount = preferenceAwarePantryMatchRows.filter((row) => row?.bucket === 'Cook Now').length
  const almostThereRows = preferenceAwarePantryMatchRows.filter((row) => row?.bucket === 'Almost There')
  const oneItemAwayCount = almostThereRows.filter((row) => {
    const matched = Number(row?.matched_count)
    const total = Number(row?.total_count)
    return Number.isFinite(matched) && Number.isFinite(total) && total - matched === 1
  }).length

  // Add Recipe inline inputs
  const [urlImportValue, setUrlImportValue] = useState('')
  const [urlImportLoading, setUrlImportLoading] = useState(false)
  const [urlImportError, setUrlImportError] = useState('')
  const [pasteImportValue, setPasteImportValue] = useState('')
  const [pasteImportLoading, setPasteImportLoading] = useState(false)
  const [pasteImportError, setPasteImportError] = useState('')
  const recipeImageInputRef = useRef(null)
  const [deleteConfirmList, setDeleteConfirmList] = useState(null) // { id, name } when showing delete confirm
  const pendingRecipeImportAfterLoginRef = useRef(false) // when true, retry URL import after user logs in
  const [retryRecipeImportUrl, setRetryRecipeImportUrl] = useState(null) // URL to import after login (triggers effect)
  const [showRecipeAuthModal, setShowRecipeAuthModal] = useState(false)
  const [recipeAuthDomain, setRecipeAuthDomain] = useState(null)
  const [recipeAuthRecipeUrl, setRecipeAuthRecipeUrl] = useState(null)

  // External callers (e.g. cold-start planner hero) can open this screen directly
  // to the Add-recipe panel by dispatching `cookSetMethod` with detail { method: 'add' | 'browse' }.
  useEffect(() => {
    const onCookSetMethod = (e) => {
      const m = e?.detail?.method
      if (m === 'add' || m === 'browse') {
        if (m === 'add' && !isAuthenticated) {
          setShowLoginModal?.(true)
          return
        }
        setGetRecipesMethod(m)
      }
    }
    window.addEventListener('cookSetMethod', onCookSetMethod)
    return () => window.removeEventListener('cookSetMethod', onCookSetMethod)
  }, [isAuthenticated, setShowLoginModal])

  // Fetch curated packs from backend
  useEffect(() => {
    if (authLoading || !isAuthenticated) return
    authenticatedFetch('/curated-packs', { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (Array.isArray(data)) setLivePacks(data) })
      .catch(() => {})
  }, [isAuthenticated, authLoading])

  useEffect(() => {
    if (getRecipesMethod !== 'add' || resolvedPacks.length === 0) return
    trackRecipePackShelfViewedOnce(
      RECIPE_PACK_SURFACES.COOK_ADD,
      { pack_count: resolvedPacks.length, library_count: fullLibraryRecipeCount },
      packShelfViewedRef,
    )
  }, [getRecipesMethod, resolvedPacks.length, fullLibraryRecipeCount])

  useEffect(() => {
    if (getRecipesMethod !== 'browse') return
    if (fullLibraryRecipeCount >= THIN_LIBRARY_THRESHOLD) return
    if (resolvedPacks.length === 0) return
    trackRecipePackShelfViewedOnce(
      RECIPE_PACK_SURFACES.COOK_BROWSE,
      { pack_count: resolvedPacks.length, library_count: fullLibraryRecipeCount },
      packShelfViewedRef,
    )
  }, [getRecipesMethod, fullLibraryRecipeCount, resolvedPacks.length])

  // Refetch when library changes from another tab (e.g. Plan → apply recipe pack)
  useEffect(() => {
    if (authLoading || !isAuthenticated) return
    const onInvalidate = () => {
      loadSavedLists()
    }
    window.addEventListener(SAVED_LISTS_INVALIDATE_EVENT, onInvalidate)
    return () => window.removeEventListener(SAVED_LISTS_INVALIDATE_EVENT, onInvalidate)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authLoading])

  // Load saved lists on component mount or when authentication state changes
  useEffect(() => {
    // Wait for auth to finish loading before attempting to fetch
    if (authLoading) {
      return
    }

    // Only fetch if user is authenticated
    if (!isAuthenticated) {
      setSavedLists([])
      return
    }

    const now = Date.now()
    const currentUserId = user?.id ?? null
    const isCacheValid = savedListsCache.timestamp &&
                         (now - savedListsCache.timestamp) < savedListsCache.cacheExpiry &&
                         savedListsCache.lists.length > 0 &&
                         savedListsCache.userId === currentUserId

    if (isCacheValid && searchTerm === '' && !showFavoritesOnly && selectedCuisines.length === 0 && selectedHealthTiers.length === 0 && selectedMaxTime == null) {
      // Use cached lists if available and no filters are active
      const sortedCachedLists = sortListsByFavoriteAndRecent(savedListsCache.lists)
      setSavedLists(sortedCachedLists)
    } else {
      // Load from server
      loadSavedLists()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authLoading, user?.id])

  async function loadSavedLists() {
    if (!isAuthenticated) {
      setSavedLists([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams()
      if (searchTerm) {
        params.set('search', searchTerm)
      }
      if (showFavoritesOnly) {
        params.set('include_favorites_only', 'true')
      }
      if (selectedCuisines.length > 0) {
        selectedCuisines.forEach(cuisine => {
          params.append('cuisine', cuisine)
        })
      }
      if (selectedHealthTiers.length > 0) {
        selectedHealthTiers.forEach(tier => {
          params.append('health_tier', tier)
        })
      }
      if (selectedMaxTime != null) {
        params.set('max_total_minutes', String(selectedMaxTime))
      }
      params.set('limit', '100')  // Increased to ensure recipes aren't cut off

      const response = await authenticatedFetch(`/saved-lists/?${params}`, {
        headers: getAuthHeaders()
      })
      
      // Handle authentication errors: show login screen instead of error message
      if (response.status === 401 || response.status === 403) {
        console.warn('[SavedListsManager] Authentication error, clearing lists and cache')
        setSavedLists([])
        invalidateSavedListsCache()
        setError('')
        setShowLoginModal?.(true)
        setLoading(false)
        return
      }
      
      if (!response.ok) {
        // Try to get error details from response
        let errorMessage = 'Failed to load saved lists'
        try {
          const errorData = await response.json()
          errorMessage = errorData.detail || errorData.message || errorMessage
        } catch {
          // If response is not JSON, try text
          try {
            const errorText = await response.text()
            if (errorText) {
              errorMessage = `${errorMessage}: ${errorText}`
            } else {
              errorMessage = `${errorMessage} (Status: ${response.status})`
            }
          } catch {
            errorMessage = `${errorMessage} (Status: ${response.status})`
          }
        }
        throw new Error(errorMessage)
      }

      const lists = await response.json()
      
      // Debug: Log recipe count and details
      const recipeCount = lists.filter(list => {
        const tags = list.tags || []
        const metadata = list.list_metadata || {}
        return tags.includes('recipe') || metadata.is_recipe === true
      }).length
      console.log(`[SavedListsManager] Loaded ${lists.length} total lists, ${recipeCount} recipes`)
      
      // Additional debugging: Log first few lists to see their structure
      if (lists.length > 0) {
        console.log('[SavedListsManager] Sample lists:', lists.slice(0, 3).map(list => ({
          id: list.id,
          name: list.name,
          tags: list.tags,
          has_recipe_tag: (list.tags || []).includes('recipe'),
          is_recipe_metadata: (list.list_metadata || {}).is_recipe,
          item_count: list.item_count
        })))
      } else {
        console.warn('[SavedListsManager] No lists returned from API. This could indicate:')
        console.warn('  1. User is not authenticated (check if token is being sent)')
        console.warn('  2. User has no saved lists')
        console.warn('  3. Lists exist but with different user_id')
        console.warn('  4. Backend query is filtering incorrectly')
        
        // Try to get diagnostics if available
        try {
          const diagResponse = await authenticatedFetch('/saved-lists/diagnostics', {
            headers: getAuthHeaders()
          })
          if (diagResponse.ok) {
            const diagnostics = await diagResponse.json()
            console.log('[SavedListsManager] Diagnostics:', diagnostics)
          }
        } catch (diagError) {
          console.debug('Could not fetch diagnostics:', diagError)
        }
      }
      
      const sortedLists = sortListsByFavoriteAndRecent(lists)
      setSavedLists(sortedLists)
      
      // Update cache only for unfiltered results (base cache), and tie cache to current user
      if (!searchTerm && !showFavoritesOnly && selectedCuisines.length === 0 && selectedHealthTiers.length === 0 && selectedMaxTime == null) {
        savedListsCache.lists = sortedLists
        savedListsCache.timestamp = Date.now()
        savedListsCache.userId = user?.id ?? null
      }
    } catch (err) {
      console.error('Error loading saved lists:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // Reload lists when search term, favorites filter, or cuisine filter changes (only when authenticated)
  useEffect(() => {
    if (authLoading || !isAuthenticated) return
    const timeoutId = setTimeout(() => {
      loadSavedLists()
    }, 600) // Debounce search - wait 600ms after user stops typing

    return () => clearTimeout(timeoutId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, showFavoritesOnly, selectedCuisines, selectedHealthTiers, selectedMaxTime, isAuthenticated, authLoading])

  // When recipe is imported via URL: clear filters, invalidate cache, refresh, and scroll to the new recipe
  useEffect(() => {
    function handleRecipeImported(e) {
      const detail = e.detail || {}
      const listId = detail.listId ?? null
      const isImageImport = detail.source === 'image'
      const shouldScroll = detail.scroll !== false
      if (listId && shouldScroll) {
        if (isMobile) queueScrollCookPageToTop()
        else setScrollToImportedListId(listId)
        setHighlightImportedListId(listId)
      }
      if (isImageImport) {
        const label = String(detail.name || 'Recipe').trim() || 'Recipe'
        setGetRecipesMethod('browse')
        showToast({
          title: `Imported “${label}”`,
          message: 'It’s with your recipes.',
          variant: 'success',
          duration: 7000,
          actionLabel: listId ? 'View recipe' : null,
          onAction: listId
            ? () => {
                setGetRecipesMethod('browse')
                setScrollToImportedListId(listId)
                setHighlightImportedListId(listId)
              }
            : null,
        })
      }
      setSearchTerm('')
      setShowFavoritesOnly(false)
      setSelectedCuisines([])
      setSelectedHealthTiers([])
      setSelectedMaxTime(null)
      invalidateSavedListsCache()
      setRefreshAfterImport(true)
    }
    window.addEventListener('recipeImported', handleRecipeImported)
    return () => window.removeEventListener('recipeImported', handleRecipeImported)
  }, [isMobile, showToast])

  // One-off refresh after recipe import (so we load with cleared filters immediately)
  useEffect(() => {
    if (!refreshAfterImport || !isAuthenticated) return
    loadSavedLists()
    setRefreshAfterImport(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshAfterImport, isAuthenticated])

  // Build query params for filter count (same shape as loadSavedLists)
  function buildFilterParams(opts) {
    const params = new URLSearchParams()
    if (opts.search) params.set('search', opts.search)
    if (opts.favorites) params.set('include_favorites_only', 'true')
    if (opts.cuisines?.length) opts.cuisines.forEach(c => params.append('cuisine', c))
    if (opts.healthTiers?.length) opts.healthTiers.forEach(t => params.append('health_tier', t))
    if (opts.maxTime != null) params.set('max_total_minutes', String(opts.maxTime))
    return params
  }

  async function fetchFilterCount(opts) {
    if (!isAuthenticated) return
    setFilterCountLoading(true)
    setFilterCount(null)
    try {
      const params = buildFilterParams(opts)
      const response = await authenticatedFetch(`/saved-lists/count?${params}`, { headers: getAuthHeaders() })
      if (response.ok) {
        const data = await response.json()
        setFilterCount(data.count ?? 0)
      }
    } catch (e) {
      console.warn('[SavedListsManager] Filter count fetch failed:', e)
      setFilterCount(null)
    } finally {
      setFilterCountLoading(false)
    }
  }

  async function fetchFilterOptions(opts) {
    if (!isAuthenticated) return
    setFilterOptionsLoading(true)
    setFilterOptions(null)
    try {
      const params = buildFilterParams(opts)
      const response = await authenticatedFetch(`/saved-lists/filter-options?${params}`, { headers: getAuthHeaders() })
      if (response.ok) {
        const data = await response.json()
        setFilterOptions(data)
      }
    } catch (e) {
      console.warn('[SavedListsManager] Filter options fetch failed:', e)
      setFilterOptions(null)
    } finally {
      setFilterOptionsLoading(false)
    }
  }

  /** Return result count for this option (0 = would yield no results, disable it). Null = unknown, don't disable. */
  function getCountForOption(dimension, value) {
    if (!filterOptions || !filterOptions[dimension]) return null
    const opt = filterOptions[dimension].find(o => (o.value === value) || (value == null && o.value == null))
    return opt != null ? opt.count : null
  }

  function disablePantryMatchMode(entrypoint = 'cook_screen_filter_chip') {
    setPantryMatchMode(false)
    setPantryMatchError('')
    trackUiEvent('cook_pantry_match_disabled', { entrypoint })
  }

  async function runDeterministicPantryMatch() {
    if (pantryMatchLoading) return
    if (!isAuthenticated) {
      setShowLoginModal?.(true)
      trackUiEvent('cook_pantry_match_needs_login', { entrypoint: 'cook_screen_filter_chip' })
      return
    }

    setPantryMatchLoading(true)
    setPantryMatchError('')
    try {
      const response = await authenticatedFetch('/recipes/pantry-match?limit=100', {
        headers: getAuthHeaders(),
      })
      if (response.status === 401 || response.status === 403) {
        setShowLoginModal?.(true)
        return
      }
      if (!response.ok) {
        const txt = await response.text()
        throw new Error(txt || 'Unable to run pantry match')
      }
      const payload = await response.json()
      const matches = Array.isArray(payload?.recipes) ? payload.recipes : []
      const byListId = {}
      const orderedIds = []
      for (const row of matches) {
        const id = row?.saved_list_id != null ? String(row.saved_list_id) : ''
        if (!id) continue
        byListId[id] = row
        orderedIds.push(id)
      }
      setPantryMatchByListId(byListId)
      setPantryMatchOrderedIds(orderedIds)
      setPantryMatchMode(true)
      onPantryShoppingBannerFromMatch?.(Boolean(payload?.show_go_shopping_banner))
      trackUiEvent('cook_pantry_match_opened', {
        entrypoint: 'cook_screen_filter_chip',
        result_count: orderedIds.length,
        pantry_item_count: Number(payload?.pantry_item_count || 0),
      })
    } catch (err) {
      console.error('[SavedListsManager] Pantry match failed:', err)
      setPantryMatchError('Pantry match is unavailable right now. Please try again.')
      trackUiEvent('cook_pantry_match_error', {
        entrypoint: 'cook_screen_filter_chip',
      })
    } finally {
      setPantryMatchLoading(false)
    }
  }

  // When modal is closed, fetch filter-options when applied filters change (so desktop dropdowns can disable 0-count options)
  useEffect(() => {
    if (showFilterModal || !isAuthenticated) return
    if (filterOptionsDebounceRef.current) clearTimeout(filterOptionsDebounceRef.current)
    filterOptionsDebounceRef.current = setTimeout(() => {
      fetchFilterOptions({
        search: searchTerm,
        favorites: showFavoritesOnly,
        cuisines: selectedCuisines,
        healthTiers: selectedHealthTiers,
        maxTime: selectedMaxTime
      })
    }, 250)
    return () => {
      if (filterOptionsDebounceRef.current) clearTimeout(filterOptionsDebounceRef.current)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilterModal, searchTerm, showFavoritesOnly, selectedCuisines, selectedHealthTiers, selectedMaxTime])

  const activeFilterCount = (showFavoritesOnly ? 1 : 0) + selectedCuisines.length + selectedHealthTiers.length + (selectedMaxTime != null ? 1 : 0)

  // Browse filter row: single-select tab, derived from the underlying filter state it controls
  // (so Refine dropdowns and tab selection can't disagree about what's active).
  const browseTab = pantryMatchMode ? 'cookable' : 'all'

  function clearBrowseQuickFilters() {
    setSearchTerm('')
    setShowFavoritesOnly(false)
    setSelectedCuisines([])
    setSelectedHealthTiers([])
    setSelectedMaxTime(null)
  }

  function handleViewRecipe(recipeId) {
    if (!recipeId || !onViewRecipe) return
    const mainEl = document.querySelector('.main-content')
    saveCookBrowseRestore({
      filters: {
        searchTerm,
        showFavoritesOnly,
        selectedCuisines,
        selectedHealthTiers,
        selectedMaxTime,
        prefFilterOn,
        pantryMatchMode,
        activePackFilter,
        getRecipesMethod,
      },
      scrollRecipeId: String(recipeId),
      scrollTop: mainEl?.scrollTop ?? 0,
    })
    onViewRecipe(String(recipeId))
  }

  function selectBrowseTab(tabId) {
    if (tabId === 'cookable') {
      clearBrowseQuickFilters()
      if (pantryMatchMode) {
        disablePantryMatchMode()
        return
      }
      void runDeterministicPantryMatch()
    }
  }

  // Only explain pantry+prefs together when Preferences are actually filtering —
  // Cookable now alone is already clear from the filter chip.
  const prefsFiltering =
    pantryMatchMode && prefFilterOn && selfPrefTokens.length > 0
  const showSmartFilterContextNote =
    prefsFiltering || selectedHealthTiers.length > 0
  const smartFilterContextBody = prefsFiltering
    ? <>Results reflect <b>your pantry</b> and enabled preferences.</>
    : <>Results reflect <b>selected preferences</b>.</>
  const browseBannerItems = [
    showSmartFilterContextNote
      ? {
          id: 'smart-filter-context',
          variant: 'ai',
          node: (
            <StateBanner
              variant="ai"
              className="cook-ai-context-banner"
              body={smartFilterContextBody}
            />
          ),
        }
      : null,
    pantryMatchMode && preferenceAwarePantryMatchRows.length > 0 && cookableNowCount === 0 && oneItemAwayCount > 0
      ? {
          id: 'cookable-now-empty',
          variant: 'ai',
          node: (
            <StateBanner
              variant="ai"
              className="cook-ai-context-banner"
              body={<>Nothing cookable right now. Closest: <b>{oneItemAwayCount} recipe{oneItemAwayCount === 1 ? '' : 's'}</b> need 1 item.</>}
            />
          ),
        }
      : null,
  ].filter(Boolean)

  const browseScrollTopFallbackRef = useRef(initialBrowseRestore?.scrollTop ?? null)
  const browseRestoreAppliedRef = useRef(false)

  useEffect(() => {
    if (browseRestoreAppliedRef.current) return
    const pending = pendingBrowseRestoreRef.current
    if (!pending || authLoading) return
    if (pending.filters?.pantryMatchMode && !isAuthenticated) return

    browseRestoreAppliedRef.current = true
    pendingBrowseRestoreRef.current = null

    if (pending.filters?.pantryMatchMode && isAuthenticated) {
      void runDeterministicPantryMatch()
    }
    if (pending.scrollRecipeId) {
      setScrollToImportedListId(pending.scrollRecipeId)
    }
  // One-shot browse restore on mount; pantry match is invoked only when restore state requires it.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runDeterministicPantryMatch is intentionally omitted to avoid re-running restore.
  }, [authLoading, isAuthenticated])

  // After lists load, scroll the newly imported recipe into view so the user sees the success
  useEffect(() => {
    if (!scrollToImportedListId || savedLists.length === 0) return
    if (pantryMatchMode && pantryMatchLoading) return
    const listExists = savedLists.some((l) => l.id === scrollToImportedListId)
    if (!listExists) {
      setScrollToImportedListId(null)
      return
    }
    if (isMobile) {
      browseScrollTopFallbackRef.current = null
      queueScrollCookPageToTop()
      setScrollToImportedListId(null)
      return
    }
    const el = document.querySelector(`[data-list-id="${scrollToImportedListId}"]`)
    if (!el) {
      const fallbackTop = browseScrollTopFallbackRef.current
      if (fallbackTop != null) {
        const mainEl = document.querySelector('.main-content')
        if (mainEl) mainEl.scrollTop = fallbackTop
      }
      browseScrollTopFallbackRef.current = null
      setScrollToImportedListId(null)
      return
    }
    browseScrollTopFallbackRef.current = null
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' })
        setScrollToImportedListId(null)
      })
    })
  }, [savedLists, scrollToImportedListId, pantryMatchMode, pantryMatchLoading, isMobile])

  useEffect(() => {
    if (!highlightImportedListId) return
    const tid = window.setTimeout(() => setHighlightImportedListId(null), 5000)
    return () => window.clearTimeout(tid)
  }, [highlightImportedListId])

  function resetAddRecipeInputs() {
    pendingRecipeImportAfterLoginRef.current = false
    setUrlImportValue('')
    setUrlImportError('')
    setPasteImportValue('')
    setPasteImportError('')
    setShowRecipeAuthModal(false)
    setRecipeAuthDomain(null)
    setRecipeAuthRecipeUrl(null)
  }

  async function handlePasteImport() {
    const text = (pasteImportValue || '').trim()
    if (!text) {
      setPasteImportError('Please paste a recipe.')
      return
    }
    if (text.length < 20) {
      setPasteImportError('Paste a fuller recipe (title, ingredients, and directions).')
      return
    }
    setPasteImportError('')
    setPasteImportLoading(true)
    trackRecipeImportEvent('recipe_import_paste_clicked', {
      surface: RECIPE_IMPORT_SURFACES.COOK_ADD,
      import_method: 'paste',
      source: RECIPE_IMPORT_SURFACES.COOK_ADD,
      text_len: text.length,
    })
    try {
      const outcome = await importRecipeFromText(text)
      if (outcome.success) {
        const listId = outcome.result?.id ?? null
        const deduplicated = outcome.result?.status === 'deduplicated'
        trackRecipeImportEvent('recipe_import_paste_succeeded', {
          surface: RECIPE_IMPORT_SURFACES.COOK_ADD,
          import_method: 'paste',
          source: RECIPE_IMPORT_SURFACES.COOK_ADD,
          recipe_id: listId,
          deduplicated: Boolean(deduplicated),
          item_count: Number(outcome.result?.item_count || 0),
        })
        if (deduplicated) {
          const label = String(outcome.result?.name || '').trim() || 'This recipe'
          showToast({
            title: `“${label}” already saved`,
            message: 'Opening your existing recipe.',
            variant: 'success',
          })
        } else {
          const label = String(outcome.result?.name || '').trim() || 'Recipe'
          showToast({
            title: `“${label}” imported`,
            message: 'Saved from pasted text.',
            variant: 'success',
          })
        }
        if (listId && !isMobile) setScrollToImportedListId(listId)
        setGetRecipesMethod('browse')
        resetAddRecipeInputs()
        await loadSavedLists()
        if (isMobile) queueScrollCookPageToTop()
      } else {
        trackRecipeImportEvent('recipe_import_paste_failed', {
          surface: RECIPE_IMPORT_SURFACES.COOK_ADD,
          import_method: 'paste',
          source: RECIPE_IMPORT_SURFACES.COOK_ADD,
          error_code: outcome.missingFields?.length ? 'quality_rejected' : 'import_failed',
          ...missingFieldsAsProps(outcome.missingFields),
        })
        // outcome.error is already a full human sentence for quality rejects.
        const errText = typeof outcome.error === 'string' && outcome.error.trim()
          ? outcome.error.trim()
          : 'Import failed.'
        setPasteImportError(errText)
      }
    } catch (e) {
      trackRecipeImportEvent('recipe_import_paste_failed', {
        surface: RECIPE_IMPORT_SURFACES.COOK_ADD,
        import_method: 'paste',
        source: RECIPE_IMPORT_SURFACES.COOK_ADD,
        error_code: 'client_error',
      })
      const msg = e && typeof e.message === 'string' ? e.message : 'Import failed.'
      // Never render raw objects / JSON blobs on the card.
      setPasteImportError(msg.startsWith('{') || msg.startsWith('[')
        ? 'Import failed. Please try again with a clearer recipe paste.'
        : msg)
    } finally {
      setPasteImportLoading(false)
    }
  }

  async function runImportForUrl(url) {
    const u = (url || '').trim()
    if (!u) {
      setUrlImportError('Please enter a recipe URL.')
      return
    }
    try {
      new URL(u)
    } catch {
      setUrlImportError('Please enter a valid URL.')
      return
    }
    setUrlImportError('')
    setUrlImportLoading(true)
    try {
      const intent = buildIntentFromUrl(u)
      const outcome = await importRecipeFromUrl(intent, () => {})
      if (outcome.success) {
        const listId = outcome.result?.id ?? null
        if (outcome.result?.status === 'deduplicated' || outcome.result?.status === 'updated') {
          const copy = formatRecipeReimportMessage(outcome.result)
          showToast({
            title: copy.title,
            message: copy.message,
            variant: 'success',
          })
        }
        if (listId && !isMobile) setScrollToImportedListId(listId)
        setGetRecipesMethod('browse')
        resetAddRecipeInputs()
        await loadSavedLists()
        if (isMobile) queueScrollCookPageToTop()
      } else {
        if (outcome.requiresAuth) {
          pendingRecipeImportAfterLoginRef.current = true
          setShowLoginModal?.(true)
          // Keep URL in inline input so we can retry after login
        } else if (isRecipeSiteAuthErrorMsg(outcome.error)) {
          const domain = parseRecipeSiteAuthDomain(outcome.error)
          setRecipeAuthDomain(domain || null)
          setRecipeAuthRecipeUrl(u)
          setShowRecipeAuthModal(true)
          setUrlImportError('This recipe site requires login. Use "Authenticate and Import" below to add your cookies, then try Import again.')
        } else {
          setUrlImportError(outcome.error || 'Import failed.')
        }
      }
    } catch (e) {
      setUrlImportError(e.message || 'Import failed.')
    } finally {
      setUrlImportLoading(false)
    }
  }

  async function handleUrlImport() {
    runImportForUrl(urlImportValue)
  }

  // ── Recipe Packs handlers ────────────────────────────────────────────────
  function openPackDetail(packId, surface = RECIPE_PACK_SURFACES.COOK_ADD) {
    const pack = resolvedPackById[packId]
    if (!pack) return
    packEventSurfaceRef.current = surface
    setOpenPackId(packId)
    const packKeys = [pack.id, pack.slug, packId].filter(Boolean)
    const alreadyAdded = packKeys.some((key) => visibleAddedPackIds.has(key))
    trackRecipePackEvent('recipe_pack_detail_opened', {
      surface,
      ...packTelemetryIds(pack),
      recipe_count: Array.isArray(pack.recipes) ? pack.recipes.length : Number(pack.n) || 0,
      already_added: alreadyAdded,
    })
  }

  function handleTogglePackFilter(packId) {
    if (packId) {
      const pack = resolvedPackById[packId]
      if (pack) {
        trackRecipePackEvent('recipe_pack_filter_clicked', {
          ...packTelemetryIds(pack),
          active: true,
        })
      }
    } else if (browsePackFilter) {
      const pack = resolvedPackById[browsePackFilter]
      if (pack) {
        trackRecipePackEvent('recipe_pack_filter_clicked', {
          ...packTelemetryIds(pack),
          active: false,
        })
      }
    }
    setActivePackFilter(packId)
  }

  async function handleAddPack(packId, surface) {
    const pack = resolvedPackById[packId]
    if (!pack) return

    const slug = pack.slug || packId
    const packRecipeCount = Array.isArray(pack.recipes) ? pack.recipes.length : Number(pack.n) || 0
    const eventSurface = surface || packEventSurfaceRef.current || RECIPE_PACK_SURFACES.COOK_ADD
    const packKeys = [pack.id, pack.slug, packId].filter(Boolean)
    const alreadyAdded = packKeys.some((key) => visibleAddedPackIds.has(key))
    const ids = packTelemetryIds(pack)

    trackRecipePackEvent('recipe_pack_apply_clicked', {
      surface: eventSurface,
      ...ids,
      already_added: alreadyAdded,
    })

    setOpenPackId(null)

    let applyFailureTracked = false

    try {
      const res = await authenticatedFetch(`/curated-packs/${slug}/apply`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        const detail = typeof data?.detail === 'string' ? data.detail : `Could not add ${pack.name}.`
        trackRecipePackEvent('recipe_pack_apply_failed', {
          surface: eventSurface,
          ...ids,
          error_code: res.status,
        })
        applyFailureTracked = true
        throw new Error(detail)
      }
      const addedCount = Number(data?.created_count ?? 0)
      const refreshedCount = Number(data?.refreshed_count ?? 0)
      const skippedCount = Number(data?.skipped_count ?? 0)
      const changedCount = addedCount + refreshedCount
      if (changedCount <= 0 && skippedCount <= 0) {
        const skipped = Array.isArray(data?.skipped) ? data.skipped : []
        const reasons = [...new Set(skipped.map((s) => s?.reason).filter(Boolean))]
        const reasonHint = reasons.length ? ` (${reasons.join(', ')})` : ''
        trackRecipePackEvent('recipe_pack_apply_failed', {
          surface: eventSurface,
          ...ids,
          error_code: 'empty_apply',
        })
        applyFailureTracked = true
        throw new Error(`No recipes were added from ${pack.name}${reasonHint}.`)
      }
      if (changedCount <= 0 && skippedCount > 0) {
        trackRecipePackEvent('recipe_pack_apply_failed', {
          surface: eventSurface,
          ...ids,
          error_code: 'all_skipped',
          skipped_count: skippedCount,
        })
        applyFailureTracked = true
        throw new Error(
          `${skippedCount} recipe${skippedCount === 1 ? '' : 's'} from ${pack.name} already with your recipes.`,
        )
      }
      const createdIds = Array.isArray(data?.created)
        ? data.created.map((item) => item?.id).filter(Boolean)
        : []
      const refreshedIds = Array.isArray(data?.refreshed)
        ? data.refreshed.map((item) => item?.id).filter(Boolean)
        : []
      const undoBatchId = typeof data?.undo_batch_id === 'string' ? data.undo_batch_id : null
      trackRecipePackEvent('recipe_pack_apply_succeeded', {
        surface: eventSurface,
        ...ids,
        created_count: addedCount,
        refreshed_count: refreshedCount,
        skipped_count: skippedCount,
      })
      setAddedPackIds(prev => new Set([...prev, pack.id, pack.slug || packId].filter(Boolean)))
      setPackToast({
        packId: pack.id || packId,
        packSlug: slug,
        addedCount,
        refreshedCount,
        createdIds,
        refreshedIds,
        undoBatchId,
      })
      setGetRecipesMethod('browse')
      notifySavedListsInvalidated()
      await loadSavedLists()
      if (isMobile) queueScrollCookPageToTop()
    } catch (err) {
      setPackToast({
        packId,
        addedCount: null,
        error: err?.message || `Could not add ${packRecipeCount || 'these'} recipes.`,
      })
      if (!applyFailureTracked) {
        trackRecipePackEvent('recipe_pack_apply_failed', {
          surface: eventSurface,
          ...ids,
          error_code: 'client_error',
        })
      }
    }
  }

  async function handleUndoPack() {
    if (!packToast) return
    const {
      packId,
      packSlug,
      createdIds = [],
      refreshedIds = [],
      undoBatchId,
    } = packToast
    if (!undoBatchId || (!createdIds.length && !refreshedIds.length)) {
      setPackToast(null)
      showToast({
        title: 'Nothing to undo',
        message: 'That pack change can no longer be undone.',
        variant: 'error',
      })
      return
    }

    const pack = resolvedPackById[packId]
    const slug = packSlug || pack?.slug || packId
    trackRecipePackEvent('recipe_pack_undo_clicked', {
      ...packTelemetryIds(pack),
      created_count: createdIds.length,
      refreshed_count: refreshedIds.length,
    })
    setUndoingPackId(packId)
    try {
      const response = await authenticatedFetch(`/curated-packs/${encodeURIComponent(slug)}/undo`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          undo_batch_id: undoBatchId,
          created_ids: createdIds,
          refreshed_ids: refreshedIds,
        }),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        const detail = typeof data?.detail === 'string' ? data.detail : 'Could not undo that pack add.'
        throw new Error(detail)
      }

      const deletedCount = Number(data?.deleted_count ?? 0)
      const restoredCount = Number(data?.restored_count ?? 0)
      const skippedCount = Number(data?.skipped_count ?? 0)
      const fullyReversed =
        deletedCount >= createdIds.length
        && restoredCount >= refreshedIds.length
        && skippedCount === 0

      if (fullyReversed) {
        const packKeys = [packId, pack?.id, pack?.slug, slug].filter(Boolean)
        setAddedPackIds(prev => {
          const next = new Set(prev)
          packKeys.forEach((key) => next.delete(key))
          return next
        })
      }

      invalidateSavedListsCache()
      notifySavedListsInvalidated()
      await loadSavedLists()
      setPackToast(null)

      if (deletedCount <= 0 && restoredCount <= 0) {
        showToast({
          title: 'Could not undo recipe pack add',
          message: 'Those recipes may have been favorited, cooked, or planned.',
          variant: 'error',
        })
        return
      }

      const parts = []
      if (deletedCount > 0) {
        parts.push(`removed ${deletedCount} recipe${deletedCount === 1 ? '' : 's'}`)
      }
      if (restoredCount > 0) {
        parts.push(`restored ${restoredCount} recipe${restoredCount === 1 ? '' : 's'}`)
      }
      const skippedHint = skippedCount > 0
        ? ` ${skippedCount} could not be undone (favorited, cooked, or planned).`
        : ''
      showToast({
        title: `Undid ${pack?.name || 'that pack'}`,
        message: `${parts.join(' and ')}.${skippedHint}`,
        variant: skippedCount > 0 ? 'error' : 'success',
      })
    } catch (err) {
      showToast({
        title: 'Could not undo recipe pack add',
        message: err?.message || 'Please try again.',
        variant: 'error',
      })
    } finally {
      setUndoingPackId(null)
    }
  }

  // ────────────────────────────────────────────────────────────────────────

  // After login, retry recipe import if we had deferred it due to requiresAuth
  useEffect(() => {
    if (!isAuthenticated || !pendingRecipeImportAfterLoginRef.current || !(urlImportValue || '').trim()) return
    const url = urlImportValue.trim()
    pendingRecipeImportAfterLoginRef.current = false
    setRetryRecipeImportUrl(url)
  }, [isAuthenticated, urlImportValue])

  useEffect(() => {
    if (!retryRecipeImportUrl) return
    const url = retryRecipeImportUrl
    setRetryRecipeImportUrl(null)
    runImportForUrl(url)
    // We intentionally only depend on retryRecipeImportUrl here so that the
    // retry uses the same closure as when the URL was captured.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryRecipeImportUrl])

  function handleRecipeImageChange(e) {
    const file = e.target.files?.[0]
    if (file) {
      resetAddRecipeInputs()
      onRecipeImageSelected?.(file)
    }
    e.target.value = ''
  }

  // Listen for logout and token refresh failure events and reset state
  useEffect(() => {
    function handleLogout() {
      // Reset saved lists state
      setSavedLists([])
      setSearchTerm('')
      setShowFavoritesOnly(false)
      setSelectedCuisines([])
      setError('')
      // Cache is already invalidated by AuthContext
    }
    
    function handleTokenRefreshFailed() {
      // When token refresh fails, clear lists and cache and show login screen
      console.warn('[SavedListsManager] Token refresh failed, clearing lists')
      setSavedLists([])
      invalidateSavedListsCache()
      setError('')
      setShowLoginModal?.(true)
    }
    
    window.addEventListener('userLoggedOut', handleLogout)
    window.addEventListener('tokenRefreshFailed', handleTokenRefreshFailed)
    return () => {
      window.removeEventListener('userLoggedOut', handleLogout)
      window.removeEventListener('tokenRefreshFailed', handleTokenRefreshFailed)
    }
  }, [setShowLoginModal])

  async function deleteList(listId) {
    setDeletingId(listId)
    try {
      const response = await authenticatedFetch(`/saved-lists/${listId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      })

      // Handle authentication errors: show login screen instead of error message
      if (response.status === 401 || response.status === 403) {
        console.warn('[SavedListsManager] Authentication error during delete')
        setSavedLists([])
        invalidateSavedListsCache()
        setError('')
        setShowLoginModal?.(true)
        return
      }

      if (!response.ok) {
        let message = 'The delete action could not be completed.'
        try {
          const data = await response.json()
          if (data.detail) message = typeof data.detail === 'string' ? data.detail : data.detail.message || message
        } catch {
          // response not JSON or no detail
        }
        throw new Error(message)
      }

      // Remove from local state
      setSavedLists(prev => prev.filter(list => list.id !== listId))
      
      // Update cache
      savedListsCache.lists = savedListsCache.lists.filter(list => list.id !== listId)
      savedListsCache.timestamp = Date.now()

      window.dispatchEvent(new CustomEvent('savedListDeleted', { detail: { listId } }))
    } catch (err) {
      console.error('Error deleting list:', err)
      alert(`Error deleting list: ${err.message}`)
    } finally {
      setDeletingId(null)
    }
  }

  async function toggleFavorite(listId) {
    try {
      const response = await authenticatedFetch(`/saved-lists/${listId}/toggle-favorite`, {
        method: 'POST',
        headers: getAuthHeaders()
      })

      // Handle authentication errors: show login screen instead of error message
      if (response.status === 401 || response.status === 403) {
        console.warn('[SavedListsManager] Authentication error during toggle favorite')
        setSavedLists([])
        invalidateSavedListsCache()
        setError('')
        setShowLoginModal?.(true)
        return
      }

      if (!response.ok) {
        throw new Error('Failed to toggle favorite')
      }

      const updatedList = await response.json()
      
      // Update local state and re-sort (favorites first, then most recent)
      setSavedLists(prev => {
        const updated = prev.map(list => 
          list.id === listId 
            ? { ...list, is_favorite: updatedList.is_favorite }
            : list
        )
        return sortListsByFavoriteAndRecent(updated)
      })
      
      // Update cache
      savedListsCache.lists = savedListsCache.lists.map(list => 
        list.id === listId 
          ? { ...list, is_favorite: updatedList.is_favorite }
          : list
      )
      savedListsCache.timestamp = Date.now()
    } catch (err) {
      console.error('Error toggling favorite:', err)
      alert(`Error updating favorite: ${err.message}`)
    }
  }

  function openEditModal(list) {
    // Extract cuisine tag from tags
    const cuisineTag = (list.tags || []).find((tag) =>
      CUISINE_OPTIONS.some((c) => c.toLowerCase() === String(tag).toLowerCase())
    ) || ''
    
    // Store full list data including metadata for recipe detection
    setEditingList({ 
      id: list.id, 
      name: list.name, 
      description: list.description || '', 
      tags: list.tags || [], 
      last_cooked_date: list.last_cooked_date || null,
      list_metadata: list.list_metadata || {},
      created_at: list.created_at || null
    })
    setEditName(list.name || '')
    setEditDescription(list.description || '')
    setEditCuisine(cuisineTag)
    setEditAuthor(list.list_metadata?.author || '')
    // Format date for input field (YYYY-MM-DD format, extract date part if it includes time)
    const dateValue = list.last_cooked_date 
      ? list.last_cooked_date.split('T')[0] 
      : ''
    setEditLastCookedDate(dateValue)
  }

  async function addRecipeToMealPlanCandidates(list) {
    if (!isAuthenticated) {
      setShowLoginModal?.(true)
      return
    }
    if (!isSavedListRecipe(list)) return
    try {
      const res = await authenticatedFetch('/meal-plans/candidate-recipes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipe_id: list.id }),
      })
      if (!res.ok) {
        const raw = await res.text().catch(() => '')
        let msg = raw?.trim() || `Request failed (${res.status})`
        try {
          const j = JSON.parse(raw)
          if (typeof j?.detail === 'string') msg = j.detail
          else if (j?.detail != null) msg = String(j.detail)
        } catch {
          /* keep msg from raw */
        }
        if (msg.length > 280) msg = `${msg.slice(0, 280)}…`
        throw new Error(msg)
      }
      trackUiEvent('cook_add_meal_plan_candidate', { recipe_id: list.id })
      setOpenOverflowId(null)
      const recipeId = String(list.id)
      setMealPlanCandidateIds((prev) => {
        const next = new Set(prev)
        next.add(recipeId)
        return next
      })
      window.dispatchEvent(new CustomEvent('mealPlanSuggestionsRefresh'))
    } catch (err) {
      console.error('addRecipeToMealPlanCandidates', err)
      showToast({
        title: 'Could not add to meal plan picks',
        message: err?.message || 'Please try again.',
        variant: 'error',
        duration: 7000,
      })
    }
  }

  function closeEditModal() {
    setEditingList(null)
    setEditName('')
    setEditDescription('')
    setEditCuisine('')
    setEditLastCookedDate('')
    setEditAuthor('')
  }

  async function saveListEdit() {
    if (!editingList) return
    
    const trimmedName = editName.trim()
    if (!trimmedName) {
      alert('Name cannot be empty.')
      return
    }
    
    setRenamingId(editingList.id)
    try {
      const updateData = { name: trimmedName }
      // Only include description if it has changed or if it's not empty
      const trimmedDescription = editDescription.trim()
      if (trimmedDescription !== (editingList.description || '')) {
        updateData.description = trimmedDescription || null
      }
      
      // Update tags: remove old cuisine tags and add new one if selected
      const currentTags = editingList.tags || []
      const tagsWithoutCuisine = currentTags.filter(
        (tag) => !CUISINE_OPTIONS.some((c) => c.toLowerCase() === String(tag).toLowerCase())
      )
      const newTags = [...tagsWithoutCuisine]
      if (editCuisine && editCuisine.trim()) {
        newTags.push(editCuisine.trim())
      }
      updateData.tags = newTags
      
      // Update last_cooked_date if changed
      const trimmedLastCookedDate = editLastCookedDate.trim()
      if (trimmedLastCookedDate !== (editingList.last_cooked_date || '')) {
        updateData.last_cooked_date = trimmedLastCookedDate || null
      }

      // Allow author/chef to be added for recipes (list_metadata.author). Only send when it changed.
      const currentAuthor = String(editingList.list_metadata?.author || '').trim()
      const trimmedAuthor = editAuthor.trim()
      if (trimmedAuthor && trimmedAuthor !== currentAuthor) {
        updateData.author = trimmedAuthor
      }
      
      const response = await authenticatedFetch(`/saved-lists/${editingList.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(updateData)
      })
      
      // Handle authentication errors: show login screen instead of error message
      if (response.status === 401 || response.status === 403) {
        console.warn('[SavedListsManager] Authentication error during update')
        setSavedLists([])
        invalidateSavedListsCache()
        setError('')
        setShowLoginModal?.(true)
        closeEditModal()
        return
      }
      
      if (!response.ok) {
        const txt = await response.text()
        throw new Error(txt || 'Failed to update list')
      }
      const updated = await response.json()
      // Update local state
      setSavedLists(prev => prev.map(l => 
        l.id === editingList.id 
          ? { ...l, name: updated.name, description: updated.description, tags: updated.tags, last_cooked_date: updated.last_cooked_date, list_metadata: updated.list_metadata ?? l.list_metadata } 
          : l
      ))
      
      // Update cache
      savedListsCache.lists = savedListsCache.lists.map(l => 
        l.id === editingList.id 
          ? { ...l, name: updated.name, description: updated.description, tags: updated.tags, last_cooked_date: updated.last_cooked_date, list_metadata: updated.list_metadata ?? l.list_metadata } 
          : l
      )
      savedListsCache.timestamp = Date.now()
      
      closeEditModal()
    } catch (err) {
      console.error('Error updating list:', err)
      alert(`Error updating list: ${err.message}`)
    } finally {
      setRenamingId(null)
    }
  }

  function formatDate(dateString) {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString()
    } catch {
      return dateString
    }
  }

  // Handle click outside filter dropdowns to close them.
  useEffect(() => {
    function handleClickOutside(event) {
      if (showCuisineDropdown && cuisineDropdownRef.current && !cuisineDropdownRef.current.contains(event.target)) {
        setShowCuisineDropdown(false)
      }
      if (showDietDropdown && dietDropdownRef.current && !dietDropdownRef.current.contains(event.target)) {
        setShowDietDropdown(false)
      }
      if (showTimeDropdown && timeDropdownRef.current && !timeDropdownRef.current.contains(event.target)) {
        setShowTimeDropdown(false)
      }
    }

    function handleEscapeKey(event) {
      if (event.key === 'Escape') {
        if (showCuisineDropdown) setShowCuisineDropdown(false)
        if (showDietDropdown) setShowDietDropdown(false)
        if (showTimeDropdown) setShowTimeDropdown(false)
      }
    }

    if (showCuisineDropdown || showDietDropdown || showTimeDropdown) {
      document.addEventListener('keydown', handleEscapeKey)
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside)
      }, 0)
      return () => {
        clearTimeout(timeoutId)
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('keydown', handleEscapeKey)
      }
    }
  }, [showCuisineDropdown, showDietDropdown, showTimeDropdown])

  // Close overflow menu on outside click or Escape (ignore mousedown inside the open menu so Edit/Delete clicks run)
  useEffect(() => {
    if (openOverflowId === null) return
    const handleClose = (e) => {
      if (e.type === 'keydown') {
        if (e.key !== 'Escape') return
        setOpenOverflowId(null)
        return
      }
      if (e.type === 'mousedown') {
        const node = e.target
        const el = node instanceof Element ? node : node.parentElement
        if (el?.closest('.card-overflow-wrap')) return
        setOpenOverflowId(null)
      }
    }
    document.addEventListener('mousedown', handleClose)
    document.addEventListener('keydown', handleClose)
    return () => {
      document.removeEventListener('mousedown', handleClose)
      document.removeEventListener('keydown', handleClose)
    }
  }, [openOverflowId])

  if (loading && savedLists.length === 0) {
    return <CookPageSkeleton isMobile={isMobile} hidePageHeader={hidePageHeader} hideGetRecipesNav={hideGetRecipesNav} />
  }

  return (
    <div className="cook-prototype-font-app cook-shell">
      {hidePageHeader ? null : (
      <header
        className={`cook-page-header${isMobile ? ' cook-page-header--mobile' : ''}`}
        aria-label="Recipes page title"
      >
        <div className="page-intro-eyebrow-slot">
          <p className="eyebrow planner-eyebrow-tight">
            {fullLibraryRecipeCount} {fullLibraryRecipeCount === 1 ? 'recipe' : 'recipes'}
          </p>
        </div>
        <h1 className="h-display cook-page-title">Recipes</h1>
      </header>
      )}
      {pageNotices}

      {/* Find saved recipes — Browse saved (search/filters + grid) or Add recipe (import). */}
      <div
        className={`cook-methods-section${isMobile ? ' cook-methods-section--mobile' : ''}`}
      >
        {hideGetRecipesNav ? null : (
        <div
          role="tablist"
          aria-label="How to get recipes"
          className="cook-sub-tab-bar"
        >
          <button
            type="button"
            role="tab"
            aria-selected={visibleGetRecipesMethod === 'browse'}
            aria-controls="get-recipes-panel-browse"
            id="get-recipes-tab-browse"
            onClick={() => setGetRecipesMethod('browse')}
            className={`cook-sub-tab ${visibleGetRecipesMethod === 'browse' ? 'active' : ''}`}
          >
            <span className="cook-sub-tab__label">Browse</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={visibleGetRecipesMethod === 'add'}
            aria-controls="get-recipes-panel-add"
            id="get-recipes-tab-add"
            onClick={() => {
              if (!isAuthenticated) {
                setShowLoginModal?.(true)
                return
              }
              setGetRecipesMethod('add')
            }}
            className={`cook-sub-tab ${visibleGetRecipesMethod === 'add' ? 'active' : ''}`}
          >
            <span className="cook-sub-tab__label">Add recipes</span>
          </button>
        </div>
        )}

        {visibleGetRecipesMethod === 'browse' && (
        <div
          id="get-recipes-panel-browse"
          className="cook-browse-panel"
          role="tabpanel"
          aria-labelledby="get-recipes-tab-browse"
        >
          <div id="cook-browse-toolbar" className="cook-browse-toolbar">
            {isMobile ? (
              <>
                <div className="cook-mobile-filter-row">
                  <div className="cook-mobile-search-bar">
                    <label className="cook-mobile-search-field">
                      <i className="fas fa-search" aria-hidden="true" />
                      <input
                        ref={mobileSearchInputRef}
                        id="cook-mobile-search-field"
                        type="text"
                        inputMode="search"
                        enterKeyHint="search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Filter recipes"
                        aria-label="Filter recipes"
                      />
                      {searchTerm ? (
                        <button
                          type="button"
                          className="cook-mobile-search-clear"
                          aria-label="Clear search"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setSearchTerm('')
                            mobileSearchInputRef.current?.focus()
                          }}
                        >
                          <i className="fas fa-times" aria-hidden="true" />
                        </button>
                      ) : null}
                    </label>
                  </div>
                  <div className="cook-mobile-filter-scroll">
                    {[
                      ...MOBILE_BROWSE_TABS.map((t) => (
                        <FilterChipToggle
                          key={t.id}
                          label={t.id === 'cookable' && pantryMatchLoading ? 'Matching…' : t.label}
                          active={browseTab === t.id}
                          aria-pressed={browseTab === t.id}
                          disabled={t.id === 'cookable' && pantryMatchLoading}
                          onClick={() => selectBrowseTab(t.id)}
                        />
                      )),
                      <button
                        key="refine-filters"
                        type="button"
                        onClick={() => setShowFilterModal(true)}
                        className={`cook-quick-filter-pill cook-mobile-filter-pill cook-mobile-filter-open-btn${activeFilterCount > 0 ? ' active' : ''}`}
                        aria-label={activeFilterCount > 0 ? `${activeFilterCount} refine filters active. Open filters` : 'Open refine filters'}
                      >
                        <i className="fas fa-filter" aria-hidden="true" />
                        Filters
                        {activeFilterCount > 0 && (
                          <span className="cook-browse-tab-count">{activeFilterCount}</span>
                        )}
                      </button>,
                    ]}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="cook-browse-filter-row">
                  <label className="cook-browse-search-field">
                    <i className="fas fa-search" aria-hidden="true" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Filter recipes"
                    />
                  </label>
                  {BROWSE_TABS.map((t) => (
                    <FilterChipToggle
                      key={t.id}
                      label={t.id === 'cookable' && pantryMatchLoading ? 'Matching…' : t.label}
                      active={browseTab === t.id}
                      aria-pressed={browseTab === t.id}
                      disabled={t.id === 'cookable' && pantryMatchLoading}
                      onClick={() => selectBrowseTab(t.id)}
                    />
                  ))}
                  <div className="cook-browse-refine">
                    <span className="cook-browse-refine-label">Refine</span>

                {/* Cooking/Prep time filter: single-select max time */}
                <div ref={timeDropdownRef} className="cook-filter-dropdown">
                  <button
                    type="button"
                    onClick={() => setShowTimeDropdown(!showTimeDropdown)}
                    className={`cook-filter-trigger${selectedMaxTime != null ? ' is-active' : ''}`}
                    aria-label="Filter by max cooking and prep time"
                    aria-expanded={showTimeDropdown}
                    aria-haspopup="listbox"
                  >
                    <span className="cook-filter-trigger-label">
                      {selectedMaxTime == null
                        ? 'Time'
                        : TIME_OPTIONS.find(o => o.value === selectedMaxTime)?.label ?? `${selectedMaxTime} min`}
                    </span>
                    <i
                      className={`fas fa-chevron-${showTimeDropdown ? 'up' : 'down'} cook-filter-trigger-caret`}
                      aria-hidden="true"
                    />
                  </button>
                  {showTimeDropdown && (
                    <div
                      role="listbox"
                      className="cook-filter-menu cook-filter-menu--compact"
                    >
                      {TIME_OPTIONS
                        .filter((opt) => selectedMaxTime === opt.value || getCountForOption('time_options', opt.value) !== 0)
                        .map((opt) => {
                        const isSelected = selectedMaxTime === opt.value
                        return (
                          <div
                            key={opt.label}
                            role="option"
                            aria-selected={isSelected}
                            onClick={() => {
                              setSelectedMaxTime(isSelected ? null : opt.value)
                              setShowTimeDropdown(false)
                            }}
                            className="cook-filter-option"
                          >
                            {opt.label}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>

                {/* Cuisine filter: multi-select */}
                <div ref={cuisineDropdownRef} className="cook-filter-dropdown">
                  <button
                    type="button"
                    onClick={() => setShowCuisineDropdown(!showCuisineDropdown)}
                    className={`cook-filter-trigger${selectedCuisines.length > 0 ? ' is-active' : ''}`}
                    aria-label="Filter by cuisine style"
                    aria-expanded={showCuisineDropdown}
                    aria-haspopup="listbox"
                  >
                    <span className="cook-filter-trigger-label">
                      {selectedCuisines.length === 0
                        ? 'Cuisine'
                        : selectedCuisines.length === 1
                          ? selectedCuisines[0]
                          : 'Cuisine'}
                    </span>
                    <i
                      className={`fas fa-chevron-${showCuisineDropdown ? 'up' : 'down'} cook-filter-trigger-caret`}
                      aria-hidden="true"
                    />
                    {selectedCuisines.length > 0 && (
                      <span
                        className="cook-filter-count-badge"
                        aria-label={`${selectedCuisines.length} cuisines selected`}
                      >
                        {selectedCuisines.length}
                      </span>
                    )}
                  </button>

                  {showCuisineDropdown && (
                    <div
                      role="listbox"
                      className="cook-filter-menu"
                    >
                      {(() => {
                        const options = filterOptions?.cuisines?.length
                          ? filterOptions.cuisines.map(o => o.value).filter(Boolean)
                          : CUISINE_OPTIONS
                        const visible = options.filter((c) => selectedCuisines.includes(c) || getCountForOption('cuisines', c) !== 0)
                        return visible.map((cuisine) => {
                        const isSelected = selectedCuisines.includes(cuisine)
                        return (
                          <div
                            key={cuisine}
                            role="option"
                            aria-selected={isSelected}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedCuisines(selectedCuisines.filter(c => c !== cuisine))
                              } else {
                                setSelectedCuisines([...selectedCuisines, cuisine])
                              }
                            }}
                            className="cook-filter-option"
                          >
                            <span>{cuisine}</span>
                          </div>
                        )
                        })
                      })()}
                      {selectedCuisines.length > 0 && (
                        <>
                          <div className="cook-filter-menu-divider"></div>
                          <button
                            type="button"
                            role="option"
                            onClick={() => setSelectedCuisines([])}
                            className="cook-filter-clear-btn"
                          >
                            Clear all
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Diet filter: multi-select health tiers */}
                <div ref={dietDropdownRef} className="cook-filter-dropdown">
                  <button
                    type="button"
                    onClick={() => setShowDietDropdown(!showDietDropdown)}
                    className={`cook-filter-trigger${selectedHealthTiers.length > 0 ? ' is-active' : ''}`}
                    aria-label="Filter by healthiness"
                    aria-expanded={showDietDropdown}
                    aria-haspopup="listbox"
                  >
                    <span className="cook-filter-trigger-label">Diet</span>
                    {selectedHealthTiers.length > 0 && (
                      <span className="cook-filter-count-badge">
                        {selectedHealthTiers.length}
                      </span>
                    )}
                    <i className={`fas fa-chevron-${showDietDropdown ? 'up' : 'down'} cook-filter-trigger-caret`} aria-hidden="true" />
                  </button>
                  {showDietDropdown && (
                    <div
                      role="listbox"
                      className="cook-filter-menu"
                    >
                      {HEALTHINESS_OPTIONS
                        .filter((opt) => selectedHealthTiers.includes(opt.slug) || getCountForOption('health_tiers', opt.slug) !== 0)
                        .map((opt) => {
                          const isSelected = selectedHealthTiers.includes(opt.slug)
                          return (
                            <div
                              key={opt.slug}
                              role="option"
                              aria-selected={isSelected}
                              onClick={() => {
                                if (isSelected) {
                                  setSelectedHealthTiers(selectedHealthTiers.filter(s => s !== opt.slug))
                                } else {
                                  setSelectedHealthTiers([...selectedHealthTiers, opt.slug])
                                }
                              }}
                              className="cook-filter-option cook-filter-option--with-icon"
                            >
                              <span aria-hidden="true">
                                {opt.hearts === 1 ? '🍟' : opt.hearts === 2 ? '💚' : '🥗'}
                              </span>
                              <span>{opt.label}</span>
                            </div>
                          )
                        })}
                      {selectedHealthTiers.length > 0 && (
                        <>
                          <div className="cook-filter-menu-divider" />
                          <button
                            type="button"
                            role="option"
                            onClick={() => setSelectedHealthTiers([])}
                            className="cook-filter-clear-btn"
                          >
                            Clear all
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
              </>
            )}
            <BrowseResultCountBar
              shown={visibleRecipeLists.length}
              total={fullLibraryRecipeCount}
              preferencesAvailable={isAuthenticated && selfPrefTokens.length > 0}
              preferencesEnabled={prefFilterOn}
              preferencesExpanded={prefPanelExpanded}
              onTogglePreferencesExpanded={() => setPrefPanelExpanded((v) => !v)}
              onTogglePreferencesEnabled={() => setPrefFilterOn((v) => !v)}
              preferenceTokens={selfPrefTokens}
              onManagePreferences={onManagePreferences}
            />
          </div>

          {pantryMatchError && (
            <StateBanner
              variant="urgent"
              size="inline"
              className="cook-notice cook-notice--pantry-match"
              role="alert"
              body={pantryMatchError}
            />
          )}

      <CookFilterModal
        open={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        onApply={({ cuisines, healthTiers, maxTime }) => {
          setSelectedCuisines(cuisines)
          setSelectedHealthTiers(healthTiers)
          setSelectedMaxTime(maxTime)
        }}
        appliedCuisines={selectedCuisines}
        appliedHealthTiers={selectedHealthTiers}
        appliedMaxTime={selectedMaxTime}
        searchTerm={searchTerm}
        showFavoritesOnly={showFavoritesOnly}
        isAuthenticated={isAuthenticated}
        filterOptions={filterOptions}
        filterCount={filterCount}
        filterCountLoading={filterCountLoading}
        onFetchFilterCount={fetchFilterCount}
        onFetchFilterOptions={fetchFilterOptions}
        getCountForOption={getCountForOption}
      />

      {error && (
        <StateBanner
          variant="urgent"
          size="inline"
          className="cook-notice cook-notice--library-error"
          role="alert"
          body={error}
        />
      )}

      {/* Saved Lists - never show list cards when not authenticated (prevents anonymous lists appearing before login) */}
      {!isAuthenticated ? (
          <div className="cook-empty-hint">
          No recipes yet. Log in to add them.
        </div>
      ) : savedLists.length === 0 ? (
        (searchTerm || showFavoritesOnly || selectedCuisines.length > 0 || selectedHealthTiers.length > 0 || selectedMaxTime != null) ? (
          <div className="cook-empty-hint">
            No recipes match.
          </div>
        ) : (
          <div className="cook-empty-first-recipe">
            <h3 className="cook-empty-first-recipe-title">
              No recipes yet.
            </h3>
            <p className="cook-empty-first-recipe-desc">
              A link or a photo is enough to start.
            </p>
            <Button
              type="button"
              onClick={() => {
                setGetRecipesMethod('add')
              }}
              className="cook-empty-first-recipe-cta"
              variant="primary"
            >
              <i className="fas fa-plus" aria-hidden="true" />
              Add recipe
            </Button>
          </div>
        )
      ) : (
        <>
        <StateBannerStack items={browseBannerItems} className="cook-ai-context-row" maxVisible={1} />
        {/* Pack filter chips — feature-flagged off by default. */}
        {!HIDE_RECIPE_PACK_FILTER_BAR && visibleAddedPackIds.size > 0 && (
          <PackFilterChips
            addedPackIds={visibleAddedPackIds}
            activePackFilter={browsePackFilter}
            onTogglePackFilter={handleTogglePackFilter}
          />
        )}

        {/* Thin-library pack prompt — hide when other header notices already occupy the band */}
        {fullLibraryRecipeCount < THIN_LIBRARY_THRESHOLD &&
          browseBannerItems.length === 0 &&
          !pageNoticesActive &&
          !pantryMatchError &&
          !error && (
          <div className="cook-thin-prompt-wrap">
            <BrowseThinPrompt
              packs={resolvedPacks}
              onOpenPack={(packId) => openPackDetail(packId, RECIPE_PACK_SURFACES.COOK_BROWSE)}
              isMobile={isMobile}
            />
          </div>
        )}

        {visibleRecipeLists.length === 0 ? (
          <div className="cook-filter-empty-state">
            <i className="fas fa-filter cook-filter-empty-icon" aria-hidden="true" />
            <p className="cook-filter-empty-title">
              No recipes match these filters
            </p>
            <p className="cook-filter-empty-desc">
              Try removing a filter or broadening your search.
            </p>
            <button
              type="button"
              onClick={() => {
                clearBrowseQuickFilters()
                if (pantryMatchMode) disablePantryMatchMode()
                setActivePackFilter(null)
                setPrefFilterOn(false)
              }}
              className="cook-clear-filters-btn"
            >
              Clear all filters
            </button>
          </div>
        ) : (
        <div className={`saved-lists-grid ${loading ? 'saved-lists-grid-reloading' : ''}`}>
          {visibleRecipeLists.map((list) => {
            // Browse grid is recipe-only (`isSavedListRecipe`); do not render legacy grocery-list cards.
            const meta = list.list_metadata || {}
            const displayCuisine = getRecipeCardDisplayCuisine(list, CUISINE_OPTIONS)
            const timeLabel = formatTotalTimeLabel(getSavedListTotalMinutes(meta))
            const thisListId = list.id != null ? String(list.id) : null
            const recipeDetailHref = thisListId ? buildRecipeDetailUrl(thisListId) : null
            const pantryMatch = thisListId ? pantryMatchByListId[thisListId] : null
            const packId = getSavedListPackId(list)
            const pack = packId ? resolvedPackById[packId] : null
            const gradientKey = getRecipeCardGradientKey(list)
            const planWeekKind = planWeekKindForRecipe(thisListId, {
              currentIds: mealPlanRecipeIds,
              nextIds: mealPlanNextWeekRecipeIds,
            })
            const isInPlan = Boolean(planWeekKind)
            const isInPicks = Boolean(thisListId && !isInPlan && mealPlanCandidateIds.has(thisListId))
            const statusChip = getRecipeCardStatusChip(pantryMatch, pantryMatchMode, {
              isInPlan,
              isInPicks,
              planWeekKind,
            })
            const showStatusChip = Boolean(statusChip && (isInPlan || isInPicks || pantryMatchMode))
            const servings = getRecipeCardServings(meta)
            const pantryMissingCount = pantryMatch
              ? Math.max(0, (pantryMatch.total_count || 0) - (pantryMatch.matched_count || 0))
              : 0

            return (
            <div
              key={list.id}
              data-list-id={list.id}
              className={`card recipe-card saved-list-card-layout${highlightImportedListId === list.id ? ' saved-list-card-import-highlight' : ''}`}
            >
                  <div className="saved-list-card-body-stack">
                    <div className="saved-list-card-body-inner">
                      <div className={`recipe-card-band recipe-card-band--${gradientKey}`}>
                        <div className="recipe-card-band-top">
                          {showStatusChip ? (
                            statusChip.variant === 'plan' ? (
                              <button
                                type="button"
                                className={`card-status-chip card-status-chip--${statusChip.variant} card-status-chip--link`}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  navigateToPlan({
                                    highlight: thisListId ? String(thisListId) : null,
                                    weekStart:
                                      planWeekKind === 'next'
                                        ? addWeeksToMonday(currentWeekStartIso(), 1)
                                        : null,
                                  })
                                }}
                                title="Open in Plan"
                              >
                                {statusChip.label}
                              </button>
                            ) : (
                              <span
                                className={`card-status-chip card-status-chip--${statusChip.variant}`}
                                role="status"
                              >
                                {statusChip.label}
                              </span>
                            )
                          ) : <span />}
                          <div className="recipe-card-band-icons">
                            <div className="card-overflow-wrap">
                              <button
                                type="button"
                                className={`card-overflow-btn card-overflow-btn--on-band${openOverflowId === list.id ? ' open' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setOpenOverflowId(openOverflowId === list.id ? null : list.id)
                                }}
                                title="More options"
                                aria-label={`More options for ${list.name}`}
                                aria-haspopup="menu"
                                aria-expanded={openOverflowId === list.id}
                              >
                                ···
                              </button>
                              {openOverflowId === list.id && (
                                <div className="overflow-menu open" role="menu">
                                  <button
                                    type="button"
                                    role="menuitem"
                                    className="overflow-menu-item"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      openEditModal(list)
                                      setOpenOverflowId(null)
                                    }}
                                    disabled={renamingId === list.id}
                                  >
                                    <i className="fas fa-pencil-alt" aria-hidden="true" />
                                    {renamingId === list.id ? 'Saving…' : 'Edit'}
                                  </button>
                                  <div className="overflow-menu-divider" />
                                  <button
                                    type="button"
                                    role="menuitem"
                                    className="overflow-menu-item danger"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setDeleteConfirmList({ id: list.id, name: list.name })
                                      setOpenOverflowId(null)
                                    }}
                                  >
                                    <i className="fas fa-trash" aria-hidden="true" />
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => toggleFavorite(list.id)}
                              className="ghost recipe-card-band-favorite saved-list-card-favorite"
                              title={list.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                              aria-label={list.is_favorite ? `Remove ${list.name} from favorites` : `Add ${list.name} to favorites`}
                              aria-pressed={list.is_favorite}
                            >
                              <i
                                className={list.is_favorite ? 'fas fa-star icon-sm' : 'far fa-star icon-sm'}
                                aria-hidden="true"
                              />
                            </button>
                          </div>
                        </div>
                        <h3 className="recipe-card-band-title">{list.name}</h3>
                        {(timeLabel || servings) && (
                          <div className="recipe-card-band-chips">
                            {timeLabel && (
                              <span className="recipe-card-band-chip">
                                <span aria-hidden="true">⏱</span>
                                {timeLabel}
                              </span>
                            )}
                            {servings && (
                              <span className="recipe-card-band-chip recipe-card-band-chip--muted">
                                {servings} srv
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="recipe-card-body">
                        {list.description && (
                          <p className="recipe-card-desc">{list.description}</p>
                        )}
                        {(list.item_count > 0 || displayCuisine) && (
                          <>
                            <div className="recipe-card-stats-divider" aria-hidden="true" />
                            <div className="recipe-card-meta-row">
                              <div className="recipe-card-meta-facts">
                                {list.item_count > 0 && (
                                  <span className="recipe-card-meta-fact">
                                    <strong>{list.item_count}</strong> ingredients
                                  </span>
                                )}
                                {displayCuisine && (
                                  <>
                                    {list.item_count > 0 && (
                                      <span className="recipe-card-meta-sep" aria-hidden="true">·</span>
                                    )}
                                    <span className="recipe-card-meta-fact">{displayCuisine}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                        {renderRecipeSourceLabel(list, pack)}
                        {pantryMatchMode && pantryMatch && (
                          <div
                            className={`recipe-card-pantry-explain${pantryMissingCount === 0 ? ' recipe-card-pantry-explain--stocked' : ''}`}
                          >
                            <span>
                              You have {pantryMatch.matched_count}/{pantryMatch.total_count} ingredients
                            </span>
                            {renderRecipePantryMissingLine(pantryMatch)}
                          </div>
                        )}
                        {renderSavedListTagSection(list, true, CUISINE_OPTIONS, pack)}
                        <div className="recipe-card-actions">
                          {recipeDetailHref && (
                            <a
                              href={recipeDetailHref}
                              className="recipe-card-btn-view"
                              title={`Open recipe details for ${list.name}`}
                              aria-label={`Open recipe details for ${list.name}`}
                              onClick={(e) => {
                                e.stopPropagation()
                                if (!onViewRecipe) return
                                e.preventDefault()
                                handleViewRecipe(thisListId)
                              }}
                            >
                              View recipe
                            </a>
                          )}
                          {!isInPlan && !isInPicks && (
                            <RecipeCardAddToPlanButton
                              isMobile={isMobile}
                              recipeName={list.name}
                              onClick={(e) => {
                                e.stopPropagation()
                                void addRecipeToMealPlanCandidates(list)
                              }}
                            />
                          )}
                          {isInPicks && (
                            <button
                              type="button"
                              className="link-cta link-cta-standalone recipe-card-btn-go-plan"
                              onClick={(e) => {
                                e.stopPropagation()
                                navigateToPlan({ highlight: thisListId })
                              }}
                            >
                              Go to Plan
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
            </div>
            )
          })}
        </div>
      )}
        </>
      )}
      <CookBrowseBackToTop enabled={visibleGetRecipesMethod === 'browse'} />
        </div>
        )}

        {visibleGetRecipesMethod === 'add' && (
          <div
            id="get-recipes-panel-add"
            className="cook-add-panel"
            role={hideGetRecipesNav ? undefined : 'tabpanel'}
            aria-labelledby={hideGetRecipesNav ? undefined : 'get-recipes-tab-add'}
          >
            <input
              ref={recipeImageInputRef}
              id="add-recipe-image-inline-input"
              type="file"
              accept="image/*"
              onChange={handleRecipeImageChange}
              className="sr-only"
              aria-label="Choose recipe photo or take a picture"
            />

            {isMobile ? (
              <MobileAddMature
                headingLevel={onboardingAdd ? 2 : (hidePageHeader ? 1 : 2)}
                onboarding={onboardingAdd}
                packs={shuffledAddPacks}
                addedPackIds={visibleAddedPackIds}
                onOpenPack={(packId) => openPackDetail(packId, RECIPE_PACK_SURFACES.COOK_ADD)}
                onAddPack={(packId) => handleAddPack(packId, RECIPE_PACK_SURFACES.COOK_ADD)}
                urlImportValue={urlImportValue}
                onUrlImportChange={setUrlImportValue}
                onUrlImport={handleUrlImport}
                urlImportLoading={urlImportLoading}
                urlImportError={urlImportError}
                pasteImportValue={pasteImportValue}
                onPasteImportChange={setPasteImportValue}
                onPasteImport={handlePasteImport}
                pasteImportLoading={pasteImportLoading}
                pasteImportError={pasteImportError}
                onPhotoImport={() => recipeImageInputRef.current?.click()}
                onConnectedAccount={() => setShowRecipeAuthModal(true)}
              />
            ) : (
              <AddViewMature
                headingLevel={onboardingAdd ? 2 : (hidePageHeader ? 1 : 2)}
                onboarding={onboardingAdd}
                packs={shuffledAddPacks}
                addedPackIds={visibleAddedPackIds}
                onOpenPack={(packId) => openPackDetail(packId, RECIPE_PACK_SURFACES.COOK_ADD)}
                onAddPack={(packId) => handleAddPack(packId, RECIPE_PACK_SURFACES.COOK_ADD)}
                urlImportValue={urlImportValue}
                onUrlImportChange={setUrlImportValue}
                onUrlImport={handleUrlImport}
                urlImportLoading={urlImportLoading}
                urlImportError={urlImportError}
                pasteImportValue={pasteImportValue}
                onPasteImportChange={setPasteImportValue}
                onPasteImport={handlePasteImport}
                pasteImportLoading={pasteImportLoading}
                pasteImportError={pasteImportError}
                onPhotoImport={() => recipeImageInputRef.current?.click()}
                onConnectedAccount={() => setShowRecipeAuthModal(true)}
              />
            )}

            {urlImportError && isMobile && (
              <div className="cook-mobile-url-error-wrap">
                <p className="cook-add-recipes-inline-error" role="alert">{urlImportError}</p>
                {!showRecipeAuthModal && recipeAuthDomain && (
                  <Button
                    type="button"
                    variant="primary"
                    className="cook-add-recipes-auth-btn"
                    onClick={() => setShowRecipeAuthModal(true)}
                  >
                    Authenticate and Import
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        open={Boolean(editingList)}
        onClose={closeEditModal}
        titleId="edit-recipe-modal-title"
        contentClassName="cook-edit-modal-content"
      >
        <ModalHeader>
          <h2 id="edit-recipe-modal-title">Edit Recipe</h2>
          <Button
            type="button"
            variant="ghost"
            className="modal-close"
            onClick={closeEditModal}
            aria-label="Close"
          >
            ×
          </Button>
        </ModalHeader>

        <ModalBody>
          {editingList && (() => {
            const tags = editingList.tags || []
            const metadata = editingList.list_metadata || {}
            const isRecipe = tags.some(t => String(t).toLowerCase() === 'recipe') ||
              metadata.is_recipe === true ||
              metadata.source_url !== undefined ||
              metadata.recipe_name !== undefined ||
              (editingList.description && editingList.description.toLowerCase().startsWith('recipe:'))
            const hasAuthor = Boolean(metadata.author && String(metadata.author).trim())

            return (
              <div className="cook-edit-form-stack">
                <div className="cook-edit-field-stack">
                  <label htmlFor="edit-name" className="cook-edit-modal-label">
                    Name <span className="cook-edit-required-mark">*</span>
                  </label>
                  <input
                    id="edit-name"
                    type="text"
                    className="input"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="e.g., Chicken Parmesan"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        saveListEdit()
                      } else if (e.key === 'Escape') {
                        closeEditModal()
                      }
                    }}
                  />
                </div>

                <div className="cook-edit-field-stack">
                  <label htmlFor="edit-description" className="cook-edit-modal-label">
                    Description
                  </label>
                  <textarea
                    id="edit-description"
                    className="input"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="Optional description..."
                    rows={3}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        closeEditModal()
                      }
                    }}
                  />
                </div>

                <div className="cook-edit-field-stack">
                  <label htmlFor="edit-cuisine" className="cook-edit-modal-label">
                    Cuisine Style (optional)
                  </label>
                  <select
                    id="edit-cuisine"
                    value={editCuisine}
                    onChange={(e) => setEditCuisine(e.target.value)}
                    className="cook-edit-select"
                  >
                    <option value="">Select cuisine style...</option>
                    {CUISINE_OPTIONS.map((cuisine) => (
                      <option key={cuisine} value={cuisine}>{cuisine}</option>
                    ))}
                  </select>
                </div>

                {isRecipe && !hasAuthor && (
                  <div className="cook-edit-field-stack">
                    <label htmlFor="edit-author" className="cook-edit-modal-label">
                      Chef/Author (optional)
                    </label>
                    <input
                      id="edit-author"
                      type="text"
                      className="input"
                      value={editAuthor}
                      onChange={(e) => setEditAuthor(e.target.value)}
                      placeholder="e.g., Ina Garten"
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') closeEditModal()
                      }}
                    />
                  </div>
                )}

                {isRecipe && (
                  <div className="cook-edit-field-stack">
                    <label htmlFor="edit-last-cooked-date" className="cook-edit-modal-label">
                      Last Cooked Date (optional)
                    </label>
                    <input
                      id="edit-last-cooked-date"
                      type="date"
                      className="input"
                      value={editLastCookedDate}
                      onChange={(e) => setEditLastCookedDate(e.target.value)}
                    />
                    <div className="cook-edit-date-actions">
                      <Button
                        type="button"
                        variant="link"
                        className="cook-edit-date-btn"
                        onClick={() => {
                          const today = new Date().toISOString().split('T')[0]
                          setEditLastCookedDate(today)
                        }}
                      >
                        Set to Today
                      </Button>
                      {editLastCookedDate && (
                        <Button
                          type="button"
                          variant="ghost"
                          className="cook-edit-date-clear"
                          onClick={() => setEditLastCookedDate('')}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {isRecipe && editingList?.created_at && (
                  <div className="cook-edit-meta-box">
                    <i className="fas fa-calendar-plus icon-xs icon-muted icon-spacing-sm" aria-hidden="true"></i>
                    <strong className="cook-edit-modal-meta-label">Added/Imported:</strong> {formatDate(editingList.created_at)}
                  </div>
                )}
              </div>
            )
          })()}
        </ModalBody>

        <ModalFooter onClick={(e) => e.stopPropagation()}>
          {editingList ? (
            <>
              <Button
                type="button"
                variant="destructive"
                onClick={() => setDeleteConfirmList({ id: editingList.id, name: editingList.name })}
                disabled={deletingId === editingList.id}
                aria-label={`Delete ${editingList.name}`}
              >
                {deletingId === editingList.id ? 'Deleting…' : 'Delete'}
              </Button>

              <div className="cook-edit-modal-footer-spacer" aria-hidden="true" />

              <div className="cook-edit-modal-footer">
                <Button type="button" variant="secondary" onClick={closeEditModal}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={saveListEdit}
                  disabled={renamingId === editingList.id || !editName.trim()}
                >
                  {renamingId === editingList.id ? (
                    <>
                      <i className="fas fa-spinner fa-spin icon-sm icon-inverse" aria-hidden="true"></i> Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </Button>
              </div>
            </>
          ) : null}
        </ModalFooter>
      </Modal>

      {/* Recipe site auth: capture cookies and import so user can retry recipe import */}
      {/* Recipe Packs — detail overlay (desktop modal / mobile sheet) */}
      {openPackId && resolvedPackById[openPackId] && (
        isMobile ? (
          <PackDetailSheet
            pack={resolvedPackById[openPackId]}
            added={visibleAddedPackIds.has(openPackId)}
            onClose={() => setOpenPackId(null)}
            onAdd={() => handleAddPack(openPackId)}
          />
        ) : (
          <PackDetailModal
            pack={resolvedPackById[openPackId]}
            added={visibleAddedPackIds.has(openPackId)}
            onClose={() => setOpenPackId(null)}
            onAdd={() => handleAddPack(openPackId)}
          />
        )
      )}

      {/* Recipe Packs — post-add toast */}
      {packToast && resolvedPackById[packToast.packId] && (
        <PackToast
          pack={resolvedPackById[packToast.packId]}
          addedCount={packToast.addedCount}
          refreshedCount={packToast.refreshedCount}
          error={packToast.error}
          onDismiss={() => setPackToast(null)}
          onUndo={handleUndoPack}
          undoing={undoingPackId === packToast.packId}
          canUndo={Boolean(
            packToast.undoBatchId
            && (
              (Array.isArray(packToast.createdIds) && packToast.createdIds.length > 0)
              || (Array.isArray(packToast.refreshedIds) && packToast.refreshedIds.length > 0)
            )
          )}
        />
      )}

      {showRecipeAuthModal && recipeAuthDomain && createPortal(
        <RecipeAuthModal
          domain={recipeAuthDomain}
          recipeUrl={recipeAuthRecipeUrl}
          onClose={() => {
            setShowRecipeAuthModal(false)
          }}
          onImported={() => {
            const url = recipeAuthRecipeUrl
            setShowRecipeAuthModal(false)
            setRecipeAuthDomain(null)
            setRecipeAuthRecipeUrl(null)
            setUrlImportError('')
            if (url) runImportForUrl(url)
          }}
        />,
        document.body
      )}

      <Modal
        open={Boolean(deleteConfirmList)}
        onClose={() => {
          if (deletingId !== deleteConfirmList?.id) setDeleteConfirmList(null)
        }}
        titleId="delete-confirm-title"
        describedById="delete-confirm-description"
        overlayClassName="modal-overlay--elevated cook-delete-modal-overlay"
        contentClassName="cook-confirm-panel"
      >
        <h2 id="delete-confirm-title" className="cook-delete-confirm-title">
          Delete &quot;{deleteConfirmList?.name}&quot;?
        </h2>
        <p id="delete-confirm-description" className="cook-delete-confirm-description">
          This cannot be undone.
        </p>
        <div className="cook-confirm-actions">
          <Button
            variant="secondary"
            onClick={() => setDeleteConfirmList(null)}
            disabled={deletingId === deleteConfirmList?.id}
            className="cook-delete-confirm-cancel-btn"
            aria-label="Cancel delete"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              if (!deleteConfirmList) return
              deleteList(deleteConfirmList.id)
              closeEditModal()
              setDeleteConfirmList(null)
            }}
            disabled={deletingId === deleteConfirmList?.id}
            className="cook-delete-confirm-delete-btn"
            aria-label={`Delete ${deleteConfirmList?.name ?? 'recipe'}`}
          >
            {deletingId === deleteConfirmList?.id ? (
              <>
                <i className="fas fa-spinner fa-spin icon-sm" aria-hidden="true" /> Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </div>
      </Modal>
    </div>
  )
}

// Memoize component to prevent unnecessary re-renders when selectedStore changes.
// onLoadList remains in the public props/memo compare for parent stability; Cook browse
// no longer pushes grocery lists via legacy cards (View / Add to Plan only).
export default memo(SavedListsManager, (prevProps, nextProps) => {
  // Only re-render if callbacks change or if selectedStore ID actually changes
  // (not just object reference). Handle null/undefined cases properly.
  const prevStoreId = prevProps.selectedStore?.id ?? null
  const nextStoreId = nextProps.selectedStore?.id ?? null

  return (
    prevProps.onLoadList === nextProps.onLoadList &&
    prevStoreId === nextStoreId &&
    prevProps.setShowLoginModal === nextProps.setShowLoginModal &&
    prevProps.onRecipeImageSelected === nextProps.onRecipeImageSelected &&
    prevProps.defaultGetRecipesMethod === nextProps.defaultGetRecipesMethod &&
    prevProps.hidePageHeader === nextProps.hidePageHeader &&
    prevProps.hideGetRecipesNav === nextProps.hideGetRecipesNav
  )
})
